import React, { useMemo, useRef, useState, useEffect } from 'react';
import { Bar, Line } from 'react-chartjs-2';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  type Plugin,
} from 'chart.js';
import { 
  fetchBalanceSheet, 
  fetchIncomeStatement, 
  fetchCashFlowStatement, 
  fetchKeyMetrics 
} from '../../services/stockApi';
import {
  fetchFiscalRoicRatios,
  fetchFiscalMarketCapRatios,
  fetchFiscalStandardizedBalanceSheet,
  fetchFiscalStandardizedCashFlowStatement,
  fetchFiscalStandardizedIncomeStatement,
  fetchFiscalStandardizedMetricsList,
} from '../../services/fiscalApi';
import { adminApi, isUnauthorized } from '../../services/adminApi';
import DataSourceAttribution from '../../components/DataSourceAttribution';
import type { SnackbarVariant } from '../../components/Snackbar';
import { RESEARCH_DESK_NOTE_CATEGORIES } from './researchDeskCategories';
import FloatingNewNoteButton from './FloatingNewNoteButton';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { applyQuillFontSizeStep, ensureQuillFontSizesRegistered } from '../../utils/quillFontSize';

ensureQuillFontSizesRegistered();

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

type KeyMetricsCrosshairState = {
  mouseX: number | null;
  mouseY: number | null;
};

const keyMetricsCrosshairState = new WeakMap<any, KeyMetricsCrosshairState>();

const getKeyMetricsCrosshairState = (chart: any): KeyMetricsCrosshairState => {
  const existing = keyMetricsCrosshairState.get(chart);
  if (existing) return existing;
  const next: KeyMetricsCrosshairState = { mouseX: null, mouseY: null };
  keyMetricsCrosshairState.set(chart, next);
  return next;
};

// Crosshair for Research → Key Metrics (simple dashed guide lines only).
// Implemented per-chart (no shared global state) to avoid the "stuck" line
// when moving between multiple charts in a grid.
const keyMetricsCrosshairPlugin: Plugin = {
  id: 'key-metrics-crosshair',
  afterInit(chart) {
    const canvas = (chart as any)?.canvas as HTMLCanvasElement | undefined;
    if (!canvas || typeof canvas.addEventListener !== 'function') return;

    const handler = () => {
      const s = getKeyMetricsCrosshairState(chart);
      s.mouseX = null;
      s.mouseY = null;
      try {
        chart.draw();
      } catch {
        // ignore redraw failures
      }
    };

    (chart as any).$keyMetricsCrosshairLeaveHandler = handler;
    canvas.addEventListener('mouseleave', handler);
    canvas.addEventListener('pointerleave', handler);
    canvas.addEventListener('mouseout', handler);
  },
  beforeDestroy(chart) {
    const canvas = (chart as any)?.canvas as HTMLCanvasElement | undefined;
    const handler = (chart as any).$keyMetricsCrosshairLeaveHandler as (() => void) | undefined;
    if (canvas && handler) {
      canvas.removeEventListener('mouseleave', handler);
      canvas.removeEventListener('pointerleave', handler);
      canvas.removeEventListener('mouseout', handler);
    }
    delete (chart as any).$keyMetricsCrosshairLeaveHandler;
  },
  afterEvent(chart, args) {
    const { event } = args;
    const s = getKeyMetricsCrosshairState(chart);
    const area = (chart as any)?.chartArea;
    if (!area) return;

    if (event.type === 'mousemove') {
      const ev = event as any;
      const x = ev?.x;
      const y = ev?.y;
      if (typeof x !== 'number' || typeof y !== 'number') return;

      // Require Y to be within plot area; allow X anywhere and clamp later.
      if (y >= area.top && y <= area.bottom) {
        s.mouseX = x;
        s.mouseY = y;
      } else {
        s.mouseX = null;
        s.mouseY = null;
      }

      chart.draw();
      return;
    }

    if (event.type === 'mouseout') {
      s.mouseX = null;
      s.mouseY = null;
      chart.draw();
    }
  },
  afterDatasetsDraw(chart) {
    const s = getKeyMetricsCrosshairState(chart);
    if (s.mouseX === null || s.mouseY === null) return;

    const ctx = (chart as any)?.ctx as CanvasRenderingContext2D | undefined;
    const area = (chart as any)?.chartArea;
    if (!ctx || !area) return;

    const { left, right, top, bottom } = area;
    const xPos = Math.max(left, Math.min(right, s.mouseX));
    const yPos = Math.max(top, Math.min(bottom, s.mouseY));

    ctx.save();
    ctx.setLineDash([5, 5]);
    ctx.strokeStyle = 'rgba(128, 128, 128, 0.8)';
    ctx.lineWidth = 1;

    ctx.beginPath();
    ctx.moveTo(xPos, top);
    ctx.lineTo(xPos, bottom);
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(left, yPos);
    ctx.lineTo(right, yPos);
    ctx.stroke();

    ctx.restore();
  },
};

interface ResearchKeyMetricsScreenProps {
  isDarkMode: boolean;
  selectedStock: string;
  isAdmin?: boolean;
  authToken?: string | null;
  authUserId?: number | null;
  onUnauthorized?: () => void;
  onNotify?: (message: string, variant?: SnackbarVariant) => void;
}

type TimeRange = 'Annual' | 'Quarterly' | 'TTM';

interface ChartData {
  labels: string[];
  datasets: any[];
}

type ChartsSnapshot = {
  cashDebtData: ChartData | null;
  revenueIncomeData: ChartData | null;
  epsData: ChartData | null;
  cashFlowData: ChartData | null;
  marginsData: ChartData | null;
  roicData: ChartData | null;
  dividendsData: ChartData | null;
  sharesData: ChartData | null;
  peRatioData: ChartData | null;
  priceFcfData: ChartData | null;
};

type CacheEntry = {
  snapshot: ChartsSnapshot;
  updatedAtMs: number;
};

const CACHE_TTL_MS = 10 * 60 * 1000;

const ResearchKeyMetricsScreen: React.FC<ResearchKeyMetricsScreenProps> = ({
  isDarkMode,
  selectedStock,
  isAdmin,
  authToken,
  authUserId,
  onUnauthorized,
  onNotify,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [selectedTimeRange, setSelectedTimeRange] = useState<TimeRange>('Annual');
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [dataSource, setDataSource] = useState<'fmp' | 'fiscal'>('fmp');
  const [roicSource, setRoicSource] = useState<'fmp' | 'fiscal'>('fmp');
  const [fiscalAvailable, setFiscalAvailable] = useState(false);
  const [chartSources, setChartSources] = useState<Record<string, 'fiscal' | 'fmp'>>({});
  const [expandedChart, setExpandedChart] = useState<string | null>(null);
  const [expandedChartSource, setExpandedChartSource] = useState<'fiscal' | 'fmp' | null>(null);
  const [savingBookmarkByChartId, setSavingBookmarkByChartId] = useState<Record<string, boolean>>({});
  const [bookmarkedByChartId, setBookmarkedByChartId] = useState<Record<string, boolean>>({});
  const [bookmarkFlashByChartId, setBookmarkFlashByChartId] = useState<Record<string, boolean>>({});
  const bookmarkFlashTimeoutsRef = useRef<Record<string, number>>({});

  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
  const [noteContent, setNoteContent] = useState('');
  const [noteTitle, setNoteTitle] = useState('');
  const NOTE_CATEGORIES = useMemo(() => [...RESEARCH_DESK_NOTE_CATEGORIES], []);
  const [noteCategory, setNoteCategory] = useState('Key Metrics');
  const [isNoteCategoryOpen, setIsNoteCategoryOpen] = useState(false);
  const noteCategoryDropdownRef = useRef<HTMLDivElement | null>(null);
  const quillRef = useRef<any>(null);

  const [showFloatingButton, setShowFloatingButton] = useState<boolean>(true);

  useEffect(() => {
    const update = () => {
      // Hide if the viewport is too narrow; otherwise keep button bottom-right.
      // We reserve right padding when showing so it never covers charts.
      const minViewportForButton = 1024;
      setShowFloatingButton(window.innerWidth >= minViewportForButton);
    };

    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  useEffect(() => {
    if (!expandedChart) setExpandedChartSource(null);
  }, [expandedChart]);

  useEffect(() => {
    if (!isNoteCategoryOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsNoteCategoryOpen(false);
    };
    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as Node | null;
      const root = noteCategoryDropdownRef.current;
      if (!target || !root) return;
      if (!root.contains(target)) setIsNoteCategoryOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onMouseDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onMouseDown);
    };
  }, [isNoteCategoryOpen]);

  useEffect(() => {
    setBookmarkedByChartId({});
    setBookmarkFlashByChartId({});
  }, [selectedStock, selectedTimeRange]);

  useEffect(() => {
    const timeouts = bookmarkFlashTimeoutsRef.current;
    return () => {
      Object.keys(timeouts).forEach((k) => {
        try {
          window.clearTimeout(timeouts[k]);
        } catch {
          // ignore
        }
      });
    };
  }, []);

  const triggerBookmarkFlash = (chartId: string) => {
    setBookmarkFlashByChartId((prev) => ({ ...prev, [chartId]: true }));

    const existing = bookmarkFlashTimeoutsRef.current[chartId];
    if (existing) {
      try {
        window.clearTimeout(existing);
      } catch {
        // ignore
      }
    }

    bookmarkFlashTimeoutsRef.current[chartId] = window.setTimeout(() => {
      setBookmarkFlashByChartId((prev) => {
        if (!prev[chartId]) return prev;
        return { ...prev, [chartId]: false };
      });
    }, 30_000);
  };

  const resolvedUserId =
    typeof authUserId === 'number' &&
    Number.isFinite(authUserId) &&
    (authUserId as number) > 0
      ? (authUserId as number)
      : undefined;
  const canPersistNotes = !!authToken && resolvedUserId != null;
  const canPersistScreenshots = !!authToken;
  const normalizeHtmlText = (raw: string) =>
    String(raw ?? '')
      .replace(/<[^>]*>/g, ' ')
      .replace(/&nbsp;/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

  const extractTitleFromContent = (html: string): string => {
    if (!html) return '';
    const blockRegex = /<(p|div|li|h1|h2|h3|h4|h5|h6)[^>]*>([\s\S]*?)<\/(p|div|li|h1|h2|h3|h4|h5|h6)>/gi;
    let match = blockRegex.exec(html);
    while (match) {
      const text = normalizeHtmlText(match[2]);
      if (text) return text.split(/\s+/).slice(0, 5).join(' ');
      match = blockRegex.exec(html);
    }
    const fallback = normalizeHtmlText(html);
    if (!fallback) return '';
    return fallback.split(/\s+/).slice(0, 5).join(' ');
  };

  const hasNoteText = normalizeHtmlText(noteContent).length > 0;
  const canAddNote = noteCategory.trim().length > 0 && hasNoteText && noteTitle.trim().length > 0;

  useEffect(() => {
    const derivedTitle = extractTitleFromContent(noteContent);
    setNoteTitle(derivedTitle);
  }, [noteContent]);

  const noteCategoryDropdownWrapStyle: React.CSSProperties = {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
  };

  const noteCategoryDropdownButtonStyle: React.CSSProperties = {
    padding: '0.5rem 2.25rem 0.5rem 1rem',
    borderRadius: '6px',
    border: '1px solid #D9D9D9',
    backgroundColor: '#F6F6F6',
    fontSize: '16px',
    fontFamily: 'Manrope, sans-serif',
    fontWeight: 500,
    lineHeight: '1',
    letterSpacing: '0.01em',
    color: '#0D0D0D',
    cursor: 'pointer',
    textAlign: 'left',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    userSelect: 'none',
    outline: 'none',
    minWidth: 220,
  };

  const noteCategoryDropdownArrowStyle: React.CSSProperties = {
    position: 'absolute',
    right: 10,
    top: '50%',
    transform: 'translateY(-50%)',
    pointerEvents: 'none',
    width: 18,
    height: 18,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const noteCategoryDropdownMenuStyle: React.CSSProperties = {
    position: 'absolute',
    top: '100%',
    left: 0,
    width: '100%',
    border: '1px solid #D9D9D9',
    borderRadius: '8px',
    backgroundColor: '#ffffff',
    padding: '0.5rem 0.75rem',
    direction: 'ltr',
    textAlign: 'left',
    zIndex: 1000,
    boxShadow: '0 8px 18px rgba(15, 23, 42, 0.08)',
    boxSizing: 'border-box',
  };

  const noteCategoryDropdownItemStyle: React.CSSProperties = {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: '0.75rem',
    padding: '0.45rem 0.2rem',
    height: '44px',
    cursor: 'pointer',
    color: '#0D0D0D',
    fontSize: '16px',
    fontFamily: 'Manrope, sans-serif',
    fontWeight: 500,
    lineHeight: '1',
    letterSpacing: '0.01em',
    userSelect: 'none',
    textAlign: 'left',
  };

  const noteCategoryDropdownCheckboxStyle: React.CSSProperties = {
    width: 18,
    height: 18,
    borderRadius: 4,
    border: '1.5px solid #2F58F2',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#ffffff',
    flex: '0 0 auto',
  };

  const noteCategoryDropdownCheckIconStyle: React.CSSProperties = {
    width: 10,
    height: 10,
    borderRadius: 2,
    backgroundColor: '#2F58F2',
  };

  const openNewNote = () => {
    setNoteCategory('Key Metrics');
    setNoteTitle('');
    setNoteContent('');
    setIsNoteModalOpen(true);
  };

  // Memoize ReactQuill modules to prevent re-initialization on every render
  const quillModules = useMemo(
    () => ({
      toolbar: {
        container: '#note-toolbar',
        handlers: {
          undo: () => {
            quillRef.current?.getEditor?.()?.history.undo();
          },
          redo: () => {
            quillRef.current?.getEditor?.()?.history.redo();
          },
        },
      },
      history: {
        delay: 1000,
        maxStack: 50,
        userOnly: true,
      },
    }),
    []
  );

  const quillFormats = useMemo(
    () => [
      'bold',
      'italic',
      'underline',
      'strike',
      'size',
      'list',
      'bullet',
      'indent',
      'link',
      'image',
      'align',
      'direction',
      'color',
      'background',
    ],
    []
  );

  useEscapeKey(Boolean(expandedChart) || isNoteModalOpen, () => {
    if (isNoteModalOpen) {
      setIsNoteModalOpen(false);
      return;
    }
    if (expandedChart) setExpandedChart(null);
  });

  // Auto-detect RTL/LTR based on content (same as Summary)
  useEffect(() => {
    if (isNoteModalOpen && quillRef.current) {
      const editor = quillRef.current.getEditor();
      const editorElement = editor.root;

      const handleTextChange = () => {
        const text = editor.getText().trim();
        if (text.length === 0) {
          const dir: 'rtl' | 'ltr' = 'ltr';
          editorElement.style.direction = dir;
          editorElement.style.textAlign = 'left';

          try {
            editor.format('direction', dir, 'silent');
            editor.format('align', 'left', 'silent');
            const len = typeof editor.getLength === 'function' ? Math.max(editor.getLength(), 1) : 1;
            editor.formatLine(0, len, { direction: dir, align: 'left' }, 'silent');
          } catch {
            // Ignore formatting failures when editor is not ready.
          }
          return;
        }

        const firstChar = text.charAt(0);
        const isRTL = /[\u0590-\u05FF\u0600-\u06FF\u0700-\u074F]/.test(firstChar);

        const dir: 'rtl' | 'ltr' = isRTL ? 'rtl' : 'ltr';
        editorElement.style.direction = dir;
        editorElement.style.textAlign = dir === 'rtl' ? 'right' : 'left';

        try {
          editor.format('direction', dir, 'silent');
          editor.format('align', dir === 'rtl' ? 'right' : 'left', 'silent');
          const len = typeof editor.getLength === 'function' ? Math.max(editor.getLength(), 1) : 1;
          editor.formatLine(0, len, { direction: dir, align: dir === 'rtl' ? 'right' : 'left' }, 'silent');
        } catch {
          // Ignore formatting failures when editor is not ready.
        }
      };

      editor.on('text-change', handleTextChange);
      return () => {
        editor.off('text-change', handleTextChange);
      };
    }
  }, [isNoteModalOpen]);

  const [epsExpandedData, setEpsExpandedData] = useState<ChartData | null>(null);
  const [epsExpandedLoading, setEpsExpandedLoading] = useState(false);

  const [roicExpandedData, setRoicExpandedData] = useState<ChartData | null>(null);
  const [roicExpandedLoading, setRoicExpandedLoading] = useState(false);

  const [peRatioExpandedData, setPeRatioExpandedData] = useState<ChartData | null>(null);
  const [peRatioExpandedLoading, setPeRatioExpandedLoading] = useState(false);

  const [sharesExpandedData, setSharesExpandedData] = useState<ChartData | null>(null);
  const [sharesExpandedLoading, setSharesExpandedLoading] = useState(false);

  const [dividendsExpandedData, setDividendsExpandedData] = useState<ChartData | null>(null);
  const [dividendsExpandedLoading, setDividendsExpandedLoading] = useState(false);

  const [priceFcfExpandedData, setPriceFcfExpandedData] = useState<ChartData | null>(null);
  const [priceFcfExpandedLoading, setPriceFcfExpandedLoading] = useState(false);

  const chartsCacheRef = useRef<Map<string, CacheEntry>>(new Map());
  const requestIdRef = useRef(0);
  const epsExpandRequestIdRef = useRef(0);
  const roicExpandRequestIdRef = useRef(0);
  const peRatioExpandRequestIdRef = useRef(0);
  const sharesExpandRequestIdRef = useRef(0);
  const dividendsExpandRequestIdRef = useRef(0);
  const priceFcfExpandRequestIdRef = useRef(0);
  
  // Visibility states for each chart series
  const [cashDebtVisible, setCashDebtVisible] = useState({ cash: true, debt: true });
  const [revenueIncomeVisible, setRevenueIncomeVisible] = useState({ revenue: true, netIncome: true, operatingIncome: true });
  const [marginsVisible, setMarginsVisible] = useState({ gross: true, operating: true, net: true, fcf: true });
  const [cashFlowVisible, setCashFlowVisible] = useState({ operating: true, free: true });
  
  // Chart data states
  const [cashDebtData, setCashDebtData] = useState<ChartData | null>(null);
  const [revenueIncomeData, setRevenueIncomeData] = useState<ChartData | null>(null);
  const [epsData, setEpsData] = useState<ChartData | null>(null);
  const [cashFlowData, setCashFlowData] = useState<ChartData | null>(null);
  const [marginsData, setMarginsData] = useState<ChartData | null>(null);
  const [roicData, setRoicData] = useState<ChartData | null>(null);
  const [dividendsData, setDividendsData] = useState<ChartData | null>(null);
  const [sharesData, setSharesData] = useState<ChartData | null>(null);
  const [peRatioData, setPeRatioData] = useState<ChartData | null>(null);
  const [priceFcfData, setPriceFcfData] = useState<ChartData | null>(null);

  useEffect(() => {
    let cancelled = false;
    const requestId = ++requestIdRef.current;
    const cacheKey = `${selectedStock}::${selectedTimeRange}`;

    const nowMs = Date.now();
    const cachedEntry = chartsCacheRef.current.get(cacheKey);
    const cached = cachedEntry?.snapshot;
    const isCacheFresh = Boolean(cachedEntry && nowMs - cachedEntry.updatedAtMs < CACHE_TTL_MS);

    if (cached) {
      setLoading(false);
      setCashDebtData(cached.cashDebtData);
      setRevenueIncomeData(cached.revenueIncomeData);
      setEpsData(cached.epsData);
      setCashFlowData(cached.cashFlowData);
      setMarginsData(cached.marginsData);
      setRoicData(cached.roicData);
      setDividendsData(cached.dividendsData);
      setSharesData(cached.sharesData);
      setPeRatioData(cached.peRatioData);
      setPriceFcfData(cached.priceFcfData);
    }

    // Fresh cache: no need to hit network.
    if (cached && isCacheFresh) {
      setIsRefreshing(false);
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }

    const run = async () => {
      // If we don't have cached data for this view, show initial loading.
      if (!cached) setLoading(true);
      setIsRefreshing(Boolean(cached));

      try {
        const snapshot = await fetchAllData(requestId, cached ?? null);
        if (cancelled || requestIdRef.current !== requestId) return;
        chartsCacheRef.current.set(cacheKey, { snapshot, updatedAtMs: Date.now() });
      } finally {
        if (!cancelled && requestIdRef.current === requestId) {
          setLoading(false);
          setIsRefreshing(false);
        }
      }
    };

    run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStock, selectedTimeRange]);

  // EPS: when expanded (Annual/Quarterly/TTM), load enough history back to 2011.
  useEffect(() => {
    let cancelled = false;
    const requestId = ++epsExpandRequestIdRef.current;

    const needsLongHistory =
      expandedChart === 'eps-chart' &&
      (selectedTimeRange === 'Annual' || selectedTimeRange === 'Quarterly' || selectedTimeRange === 'TTM');

    if (!needsLongHistory) {
      setEpsExpandedLoading(false);
      setEpsExpandedData(null);
      return () => {
        cancelled = true;
      };
    }

    const run = async () => {
      setEpsExpandedLoading(true);
      setEpsExpandedData(null);

      try {
        const period = selectedTimeRange === 'Annual' ? 'annual' : 'quarter';
        const limit = selectedTimeRange === 'TTM' || selectedTimeRange === 'Quarterly' ? 80 : 20;

        const [incomeForEpsFmp, fiscalIncome] = await Promise.all([
          fetchIncomeStatement(selectedStock, period as any, limit),
          fiscalAvailable
            ? getFiscalCatalog().then((catalog) => fetchFiscalStandardizedIncomeStatement(selectedStock, catalog))
            : Promise.resolve(null),
        ]);
        if (cancelled || epsExpandRequestIdRef.current !== requestId) return;

        const toFinite = (value: unknown): number | null => {
          const num = typeof value === 'number' ? value : Number(value);
          return Number.isFinite(num) ? num : null;
        };

        const fiscalRows = fiscalAvailable
          ? (period === 'annual' ? fiscalIncome?.annual ?? [] : fiscalIncome?.quarter ?? [])
          : [];
        const useFiscal = fiscalAvailable;
        const primaryRows = useFiscal ? fiscalRows : (incomeForEpsFmp ?? []);
        setExpandedChartSource(useFiscal ? 'fiscal' : 'fmp');

        const minHistoryDate = new Date('2011-01-01');
        const reversed = [...primaryRows].reverse();
        const series = reversed.filter((is: any) => {
          const d = new Date(is?.date);
          return Number.isFinite(d.getTime()) && d >= minHistoryDate;
        });

        const next: ChartData | null = (() => {
          if (!series.length) return null;
          if (selectedTimeRange === 'TTM') {
            const epsQ = series.map((is: any) => toFinite(is?.epsDiluted));
            return {
              labels: series.map((is: any) => formatPeriodLabel(is.date)).slice(3),
              datasets: [{ label: 'EPS', data: rollingTTMSumNullable(epsQ), backgroundColor: '#FF8563', borderColor: '#FF8563', fill: false }],
            };
          }
          return {
            labels: series.map((is: any) => formatPeriodLabel(is.date)),
            datasets: [{
              label: 'EPS',
              data: series.map((is: any) => {
                const value = toFinite(is?.epsDiluted);
                return value == null ? null : value;
              }),
              backgroundColor: '#FF8563',
              borderColor: '#FF8563',
              fill: false,
            }],
          };
        })();

        setEpsExpandedData(next);
      } catch (e) {
        console.error('❌ Error loading expanded EPS history:', e);
      } finally {
        if (!cancelled && epsExpandRequestIdRef.current === requestId) {
          setEpsExpandedLoading(false);
        }
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [expandedChart, selectedTimeRange, selectedStock]);

  // Price / FCF: when expanded (Annual/Quarterly/TTM), load enough history back to 2011.
  useEffect(() => {
    let cancelled = false;
    const requestId = ++priceFcfExpandRequestIdRef.current;

    const needsLongHistory =
      expandedChart === 'price-fcf-chart' &&
      (selectedTimeRange === 'Annual' || selectedTimeRange === 'Quarterly' || selectedTimeRange === 'TTM');

    if (!needsLongHistory) {
      setPriceFcfExpandedLoading(false);
      setPriceFcfExpandedData(null);
      return () => {
        cancelled = true;
      };
    }

    const run = async () => {
      setPriceFcfExpandedLoading(true);
      setPriceFcfExpandedData(null);

      try {
        const period = selectedTimeRange === 'Annual' ? 'annual' : 'quarter';
        const limit = selectedTimeRange === 'TTM' || selectedTimeRange === 'Quarterly' ? 80 : 20;

        const [keyMetricsForPfcf, cashFlowForPfcfFmp, fiscalCash, fiscalMarketCap] = await Promise.all([
          fetchKeyMetrics(selectedStock, period as any, limit),
          fetchCashFlowStatement(selectedStock, period as any, limit),
          fiscalAvailable
            ? getFiscalCatalog().then((catalog) => fetchFiscalStandardizedCashFlowStatement(selectedStock, catalog))
            : Promise.resolve(null),
          fiscalAvailable ? fetchFiscalMarketCapRatios(selectedStock) : Promise.resolve([]),
        ]);

        if (cancelled || priceFcfExpandRequestIdRef.current !== requestId) return;

        const toFinite = (value: unknown): number | null => {
          const num = typeof value === 'number' ? value : Number(value);
          return Number.isFinite(num) ? num : null;
        };

        const getOcfValue = (row: any): number | null => {
          const ocf = toFinite(row?.operatingCashFlow);
          if (ocf != null) return ocf;
          return toFinite(row?.netCashProvidedByOperatingActivities);
        };

        const getCapexValue = (row: any): number | null => {
          const capex = toFinite(row?.capitalExpenditure);
          if (capex != null) return capex;
          return toFinite(row?.investmentsInPropertyPlantAndEquipment);
        };

        const getFcfValue = (row: any): number | null => {
          const freeCash = toFinite(row?.freeCashFlow);
          if (freeCash != null) return freeCash;
          const ocf = getOcfValue(row);
          const capex = getCapexValue(row);
          if (ocf == null || capex == null) return null;
          return ocf + capex;
        };

        const fiscalRows = fiscalAvailable
          ? (period === 'annual' ? fiscalCash?.annual ?? [] : fiscalCash?.quarter ?? [])
          : [];
        const useFiscal = fiscalAvailable;
        const primaryCashRows = useFiscal ? fiscalRows : (cashFlowForPfcfFmp ?? []);
        setExpandedChartSource(useFiscal ? 'fiscal' : 'fmp');

        const minHistoryDate = new Date('2011-01-01');
        const reversedCF = [...primaryCashRows].reverse();
        const cfSeries = reversedCF.filter((cf: any) => {
          const d = new Date(cf?.date);
          return Number.isFinite(d.getTime()) && d >= minHistoryDate;
        });

        const next: ChartData | null = (() => {
          if (!cfSeries.length) return null;

          if (useFiscal) {
            const periodKey = selectedTimeRange === 'Annual' ? 'annual' : selectedTimeRange === 'Quarterly' ? 'quarter' : 'ltm';
            const marketCapSeries = (fiscalMarketCap ?? []).filter((row: any) => row.periodType === periodKey);
            const marketCapMap = buildValueMapByDate(marketCapSeries as Array<{ date?: string; value?: number }>);
            const labels = cfSeries.map((cf: any) => formatPeriodLabel(cf.date));
            const marketCaps = cfSeries.map((cf: any) => {
              const key = buildDateKey(cf?.date);
              return key ? marketCapMap.get(key) ?? null : null;
            });

            if (selectedTimeRange === 'TTM') {
              const fcfQ = cfSeries.map((cf: any) => getFcfValue(cf));
              const fcfTTM = rollingTTMSumNullable(fcfQ);
              const ttmLabels = labels.slice(3);
              const priceFcfSeries = fcfTTM.map((ttmFcf, idx) => {
                const marketCap = marketCaps[idx + 3];
                if (marketCap == null || ttmFcf == null || ttmFcf <= 0) return null;
                return marketCap / ttmFcf;
              });
              return {
                labels: ttmLabels,
                datasets: [{ label: 'Price / FCF', data: priceFcfSeries, backgroundColor: '#BA68C8' }],
              };
            }

            const priceFcfSeries = cfSeries.map((cf: any, idx: number) => {
              const marketCap = marketCaps[idx];
              const fcf = getFcfValue(cf);
              if (marketCap == null || fcf == null || fcf <= 0) return null;
              return marketCap / fcf;
            });

            return {
              labels,
              datasets: [{ label: 'Price / FCF', data: priceFcfSeries, backgroundColor: '#BA68C8' }],
            };
          }

          const reversedKM = [...(keyMetricsForPfcf ?? [])].reverse();
          const kmSeries = reversedKM.filter((km: any) => {
            const d = new Date(km?.date);
            return Number.isFinite(d.getTime()) && d >= minHistoryDate;
          });

          const alignedLength = Math.min(kmSeries.length, cfSeries.length);
          const kmAligned = kmSeries.slice(0, alignedLength);
          const cfAligned = cfSeries.slice(0, alignedLength);

          if (!kmAligned.length || !cfAligned.length) return null;

          if (selectedTimeRange === 'TTM') {
            const marketCapQ = kmAligned.map((km: any) => toFinite(km?.marketCap));
            const fcfQ = cfAligned.map((cf: any) => getFcfValue(cf));
            const fcfTTM = rollingTTMSumNullable(fcfQ);
            const labels = cfAligned.map((cf: any) => formatPeriodLabel(cf.date)).slice(3);
            const priceFcfSeries = fcfTTM.map((ttmFcf, idx) => {
              const marketCap = marketCapQ[idx + 3];
              if (marketCap == null || ttmFcf == null || ttmFcf <= 0) return null;
              return marketCap / ttmFcf;
            });
            return {
              labels,
              datasets: [{ label: 'Price / FCF', data: priceFcfSeries, backgroundColor: '#BA68C8' }],
            };
          }

          const labels = cfAligned.map((cf: any) => formatPeriodLabel(cf.date));
          const priceFcfSeries = cfAligned.map((cf: any, idx: number) => {
            const marketCap = toFinite(kmAligned[idx]?.marketCap);
            const fcf = getFcfValue(cf);
            if (marketCap == null || fcf == null || fcf <= 0) return null;
            return marketCap / fcf;
          });

          return {
            labels,
            datasets: [{ label: 'Price / FCF', data: priceFcfSeries, backgroundColor: '#BA68C8' }],
          };
        })();

        setPriceFcfExpandedData(next);
      } catch (e) {
        console.error('❌ Error loading expanded Price / FCF history:', e);
      } finally {
        if (!cancelled && priceFcfExpandRequestIdRef.current === requestId) {
          setPriceFcfExpandedLoading(false);
        }
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [expandedChart, selectedTimeRange, selectedStock]);

  // Dividends: when expanded (Annual/Quarterly/TTM), load enough history back to 2011.
  useEffect(() => {
    let cancelled = false;
    const requestId = ++dividendsExpandRequestIdRef.current;

    const needsLongHistory =
      expandedChart === 'dividends-chart' &&
      (selectedTimeRange === 'Annual' || selectedTimeRange === 'Quarterly' || selectedTimeRange === 'TTM');

    if (!needsLongHistory) {
      setDividendsExpandedLoading(false);
      setDividendsExpandedData(null);
      return () => {
        cancelled = true;
      };
    }

    const run = async () => {
      setDividendsExpandedLoading(true);
      setDividendsExpandedData(null);

      try {
        const period = selectedTimeRange === 'Annual' ? 'annual' : 'quarter';
        const limit = selectedTimeRange === 'TTM' || selectedTimeRange === 'Quarterly' ? 80 : 20;

        const [cashFlowForDividendsFmp, fiscalCash] = await Promise.all([
          fetchCashFlowStatement(selectedStock, period as any, limit),
          fiscalAvailable
            ? getFiscalCatalog().then((catalog) => fetchFiscalStandardizedCashFlowStatement(selectedStock, catalog))
            : Promise.resolve(null),
        ]);
        if (cancelled || dividendsExpandRequestIdRef.current !== requestId) return;

        const toFinite = (value: unknown): number | null => {
          const num = typeof value === 'number' ? value : Number(value);
          return Number.isFinite(num) ? num : null;
        };

        const getDividendValue = (row: any): number | null => {
          const common = toFinite((row as any)?.commonDividendsPaid);
          if (common != null) return common;
          const net = toFinite((row as any)?.netDividendsPaid);
          if (net != null) return net;
          return toFinite(row?.dividendsPaid);
        };

        const fiscalRows = fiscalAvailable
          ? (period === 'annual' ? fiscalCash?.annual ?? [] : fiscalCash?.quarter ?? [])
          : [];
        const useFiscal = fiscalAvailable;
        const primaryRows = useFiscal ? fiscalRows : (cashFlowForDividendsFmp ?? []);
        setExpandedChartSource(useFiscal ? 'fiscal' : 'fmp');

        const minHistoryDate = new Date('2011-01-01');
        const reversed = [...primaryRows].reverse();
        const series = reversed.filter((cf: any) => {
          const d = new Date(cf?.date);
          return Number.isFinite(d.getTime()) && d >= minHistoryDate;
        });

        const next: ChartData | null = (() => {
          if (!series.length) return null;

          if (selectedTimeRange === 'TTM') {
            const dividendsQ = series.map((cf: any) => {
              const value = getDividendValue(cf);
              return value == null ? null : Math.abs(value);
            });

            return {
              labels: series.map((cf: any) => formatPeriodLabel(cf.date)).slice(3),
              datasets: [
                {
                  label: 'Dividends',
                  data: rollingTTMSumNullable(dividendsQ).map((v) => (v == null ? null : v / 1000000)),
                  backgroundColor: '#DCE775',
                },
              ],
            };
          }

          return {
            labels: series.map((cf: any) => formatPeriodLabel(cf.date)),
            datasets: [
              {
                label: 'Dividends',
                data: series.map((cf: any) => {
                  const value = getDividendValue(cf);
                  return value == null ? null : Math.abs(value) / 1000000;
                }),
                backgroundColor: '#DCE775',
              },
            ],
          };
        })();

        setDividendsExpandedData(next);
      } catch (e) {
        console.error('❌ Error loading expanded Dividends history:', e);
      } finally {
        if (!cancelled && dividendsExpandRequestIdRef.current === requestId) {
          setDividendsExpandedLoading(false);
        }
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [expandedChart, selectedTimeRange, selectedStock]);

  // Weighted Avg Shares (Diluted): when expanded (Annual/Quarterly/TTM), load enough history back to 2011.
  useEffect(() => {
    let cancelled = false;
    const requestId = ++sharesExpandRequestIdRef.current;

    const needsLongHistory =
      expandedChart === 'shares-chart' &&
      (selectedTimeRange === 'Annual' || selectedTimeRange === 'Quarterly' || selectedTimeRange === 'TTM');

    if (!needsLongHistory) {
      setSharesExpandedLoading(false);
      setSharesExpandedData(null);
      return () => {
        cancelled = true;
      };
    }

    const run = async () => {
      setSharesExpandedLoading(true);
      setSharesExpandedData(null);

      try {
        const period = selectedTimeRange === 'Annual' ? 'annual' : 'quarter';
        const limit = selectedTimeRange === 'TTM' || selectedTimeRange === 'Quarterly' ? 80 : 20;

        const [incomeForSharesFmp, fiscalIncome] = await Promise.all([
          fetchIncomeStatement(selectedStock, period as any, limit),
          fiscalAvailable
            ? getFiscalCatalog().then((catalog) => fetchFiscalStandardizedIncomeStatement(selectedStock, catalog))
            : Promise.resolve(null),
        ]);
        if (cancelled || sharesExpandRequestIdRef.current !== requestId) return;

        const toFinite = (value: unknown): number | null => {
          const num = typeof value === 'number' ? value : Number(value);
          return Number.isFinite(num) ? num : null;
        };

        const getSharesValue = (row: any): number | null =>
          toFinite(row?.weightedAverageShsOutDil ?? row?.dilutedWeightedAverageSharesOutstanding);

        const fiscalRows = fiscalAvailable
          ? (period === 'annual' ? fiscalIncome?.annual ?? [] : fiscalIncome?.quarter ?? [])
          : [];
        const useFiscal = fiscalAvailable;
        const primaryRows = useFiscal ? fiscalRows : (incomeForSharesFmp ?? []);
        setExpandedChartSource(useFiscal ? 'fiscal' : 'fmp');

        const minHistoryDate = new Date('2011-01-01');
        const reversed = [...primaryRows].reverse();
        const series = reversed.filter((is: any) => {
          const d = new Date(is?.date);
          return Number.isFinite(d.getTime()) && d >= minHistoryDate;
        });

        const next: ChartData | null = (() => {
          if (!series.length) return null;

          if (selectedTimeRange === 'TTM') {
            const sharesQ = series.map((is: any) => getSharesValue(is));
            const sharesTTMAvg = rollingTTMSumNullable(sharesQ).map((v) => (v == null ? null : v / 4));
            return {
              labels: series.map((is: any) => formatPeriodLabel(is.date)).slice(3),
              datasets: [
                {
                  label: 'Weighted Avg Shares (Diluted)',
                  data: sharesTTMAvg.map((v) => (v == null ? null : v / 1000000000)),
                  backgroundColor: '#E91E63',
                },
              ],
            };
          }

          return {
            labels: series.map((is: any) => formatPeriodLabel(is.date)),
            datasets: [
              {
                label: 'Weighted Avg Shares (Diluted)',
                data: series.map((is: any) => {
                  const value = getSharesValue(is);
                  return value == null ? null : value / 1000000000;
                }),
                backgroundColor: '#E91E63',
              },
            ],
          };
        })();

        setSharesExpandedData(next);
      } catch (e) {
        console.error('❌ Error loading expanded Shares (Diluted) history:', e);
      } finally {
        if (!cancelled && sharesExpandRequestIdRef.current === requestId) {
          setSharesExpandedLoading(false);
        }
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [expandedChart, selectedTimeRange, selectedStock]);

  // PE Ratio: when expanded (Annual/Quarterly/TTM), load enough history back to 2011.
  useEffect(() => {
    let cancelled = false;
    const requestId = ++peRatioExpandRequestIdRef.current;

    const needsLongHistory =
      expandedChart === 'pe-ratio-chart' &&
      (selectedTimeRange === 'Annual' || selectedTimeRange === 'Quarterly' || selectedTimeRange === 'TTM');

    if (!needsLongHistory) {
      setPeRatioExpandedLoading(false);
      setPeRatioExpandedData(null);
      return () => {
        cancelled = true;
      };
    }

    const run = async () => {
      setPeRatioExpandedLoading(true);
      setPeRatioExpandedData(null);

      try {
        const period = selectedTimeRange === 'Annual' ? 'annual' : 'quarter';
        const limit = selectedTimeRange === 'TTM' || selectedTimeRange === 'Quarterly' ? 80 : 20;

        const [keyMetricsForPe, incomeForPeFmp, fiscalIncome, fiscalMarketCap] = await Promise.all([
          fetchKeyMetrics(selectedStock, period as any, limit),
          fetchIncomeStatement(selectedStock, period as any, limit),
          fiscalAvailable
            ? getFiscalCatalog().then((catalog) => fetchFiscalStandardizedIncomeStatement(selectedStock, catalog))
            : Promise.resolve(null),
          fiscalAvailable ? fetchFiscalMarketCapRatios(selectedStock) : Promise.resolve([]),
        ]);

        if (cancelled || peRatioExpandRequestIdRef.current !== requestId) return;

        const fiscalRows = fiscalAvailable
          ? (period === 'annual' ? fiscalIncome?.annual ?? [] : fiscalIncome?.quarter ?? [])
          : [];
        const useFiscal = fiscalAvailable;
        const primaryIncomeRows = useFiscal ? fiscalRows : (incomeForPeFmp ?? []);
        setExpandedChartSource(useFiscal ? 'fiscal' : 'fmp');

        const minHistoryDate = new Date('2011-01-01');
        const reversedIncome = [...primaryIncomeRows].reverse();
        const incomeSeries = reversedIncome.filter((is: any) => {
          const d = new Date(is?.date);
          return Number.isFinite(d.getTime()) && d >= minHistoryDate;
        });

        const next: ChartData | null = (() => {
          if (!incomeSeries.length) return null;

          if (useFiscal) {
            const periodKey = selectedTimeRange === 'Annual' ? 'annual' : selectedTimeRange === 'Quarterly' ? 'quarter' : 'ltm';
            const marketCapSeries = (fiscalMarketCap ?? []).filter((row: any) => row.periodType === periodKey);
            const marketCapMap = buildValueMapByDate(marketCapSeries as Array<{ date?: string; value?: number }>);
            const labels = incomeSeries.map((is: any) => formatPeriodLabel(is.date));
            const marketCaps = incomeSeries.map((is: any) => {
              const key = buildDateKey(is?.date);
              return key ? marketCapMap.get(key) ?? null : null;
            });

            if (selectedTimeRange === 'TTM') {
              const netIncomeQ = incomeSeries.map((is: any) => {
                const value = Number(is?.netIncome);
                return Number.isFinite(value) ? value : null;
              });
              const netIncomeTTM = rollingTTMSumNullable(netIncomeQ);
              const ttmLabels = labels.slice(3);
              const peRatioSeries = netIncomeTTM.map((ttmNI, idx) => {
                const marketCap = marketCaps[idx + 3];
                if (marketCap == null || ttmNI == null || ttmNI <= 0) return null;
                return marketCap / ttmNI;
              });
              return {
                labels: ttmLabels,
                datasets: [
                  {
                    label: 'PE Ratio',
                    data: peRatioSeries,
                    backgroundColor: 'rgba(47, 88, 242, 0.2)',
                    borderColor: '#2F58F2',
                    fill: true,
                  },
                ],
              };
            }

            const peRatioSeries = incomeSeries.map((is: any, index: number) => {
              const marketCap = marketCaps[index];
              const netIncome = Number(is?.netIncome);
              if (marketCap == null || !Number.isFinite(netIncome) || netIncome <= 0) return null;
              return marketCap / netIncome;
            });

            return {
              labels,
              datasets: [
                {
                  label: 'PE Ratio',
                  data: peRatioSeries,
                  backgroundColor: 'rgba(47, 88, 242, 0.2)',
                  borderColor: '#2F58F2',
                  fill: true,
                },
              ],
            };
          }

          const reversedMetrics = [...(keyMetricsForPe ?? [])].reverse();
          const metricsSeries = reversedMetrics.filter((km: any) => {
            const d = new Date(km?.date);
            return Number.isFinite(d.getTime()) && d >= minHistoryDate;
          });

          const alignedLength = Math.min(metricsSeries.length, incomeSeries.length);
          const metricsAligned = metricsSeries.slice(0, alignedLength);
          const incomeAligned = incomeSeries.slice(0, alignedLength);

          if (!metricsAligned.length || !incomeAligned.length) return null;

          if (selectedTimeRange === 'TTM') {
            const marketCapQ = metricsAligned.map((km: any) => km.marketCap || 0);
            const netIncomeQ = incomeAligned.map((is: any) => {
              const value = Number(is?.netIncome);
              return Number.isFinite(value) ? value : null;
            });
            const netIncomeTTM = rollingTTMSumNullable(netIncomeQ);
            const labels = incomeAligned.map((is: any) => formatPeriodLabel(is.date)).slice(3);
            const peRatioSeries = netIncomeTTM.map((ttmNI, idx) => {
              const marketCap = marketCapQ[idx + 3] || 0;
              if (!marketCap || ttmNI == null || ttmNI <= 0) return null;
              return marketCap / ttmNI;
            });
            return {
              labels,
              datasets: [
                {
                  label: 'PE Ratio',
                  data: peRatioSeries,
                  backgroundColor: 'rgba(47, 88, 242, 0.2)',
                  borderColor: '#2F58F2',
                  fill: true,
                },
              ],
            };
          }

          const labels = metricsAligned.map((km: any) => formatPeriodLabel(km.date));
          const peRatioSeries = metricsAligned.map((km: any, index: number) => {
            const marketCap = km.marketCap;
            const netIncome = Number(incomeAligned[index]?.netIncome);
            if (!Number.isFinite(netIncome) || !marketCap || netIncome <= 0) return null;
            return marketCap / netIncome;
          });

          return {
            labels,
            datasets: [
              {
                label: 'PE Ratio',
                data: peRatioSeries,
                backgroundColor: 'rgba(47, 88, 242, 0.2)',
                borderColor: '#2F58F2',
                fill: true,
              },
            ],
          };
        })();

        setPeRatioExpandedData(next);
      } catch (e) {
        console.error('❌ Error loading expanded PE Ratio history:', e);
      } finally {
        if (!cancelled && peRatioExpandRequestIdRef.current === requestId) {
          setPeRatioExpandedLoading(false);
        }
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [expandedChart, selectedTimeRange, selectedStock]);

  // ROIC: when expanded (Annual/Quarterly/TTM), load enough history back to 2011.
  useEffect(() => {
    let cancelled = false;
    const requestId = ++roicExpandRequestIdRef.current;

    const needsLongHistory =
      expandedChart === 'roic-chart' &&
      (selectedTimeRange === 'Annual' || selectedTimeRange === 'Quarterly' || selectedTimeRange === 'TTM');

    if (!needsLongHistory) {
      setRoicExpandedLoading(false);
      setRoicExpandedData(null);
      return () => {
        cancelled = true;
      };
    }

    const run = async () => {
      setRoicExpandedLoading(true);
      setRoicExpandedData(null);

      try {
        const period = selectedTimeRange === 'Annual' ? 'annual' : 'quarter';
        const limit = selectedTimeRange === 'TTM' || selectedTimeRange === 'Quarterly' ? 80 : 20;

        const [fiscalRoic, keyMetricsForRoic] = await Promise.all([
          fiscalAvailable ? fetchFiscalRoicRatios(selectedStock) : Promise.resolve([]),
          fetchKeyMetrics(selectedStock, period as any, limit),
        ]);
        if (cancelled || roicExpandRequestIdRef.current !== requestId) return;

        const minHistoryDate = new Date('2011-01-01');
        const fiscalSeries = (fiscalRoic ?? [])
          .filter((row) => row?.date)
          .map((row) => ({
            date: String(row.date),
            periodType: String(row.periodType || ''),
            value: toFiniteNumber(row.value) ?? 0,
          }))
          .filter((row) => {
            const d = new Date(row.date);
            return Number.isFinite(d.getTime()) && d >= minHistoryDate;
          });

        const useFiscal = fiscalAvailable;
        setExpandedChartSource(useFiscal ? 'fiscal' : 'fmp');

        const reversed = [...(keyMetricsForRoic ?? [])].reverse();
        const series = reversed.filter((km: any) => {
          const d = new Date(km?.date);
          return Number.isFinite(d.getTime()) && d >= minHistoryDate;
        });

        const next: ChartData | null = (() => {
          const toPercent = (value: number) => {
            if (!Number.isFinite(value) || value === 0) return 0;
            return Math.abs(value) < 1 ? value * 100 : value;
          };

          if (fiscalAvailable) {
            if (!fiscalSeries.length) return null;
            if (selectedTimeRange === 'TTM') {
              const ltmSeries = fiscalSeries.filter((row) => row.periodType.toLowerCase().includes('ltm'));
              if (!ltmSeries.length) return null;
              return {
                labels: ltmSeries.map((row) => formatPeriodLabel(row.date)),
                datasets: [{ label: 'ROIC', data: ltmSeries.map((row) => toPercent(row.value)), backgroundColor: '#8BC34A' }],
              };
            }

            const target = selectedTimeRange === 'Annual' ? 'annual' : 'quarter';
            const scoped = fiscalSeries.filter((row) => row.periodType.toLowerCase().includes(target));
            if (!scoped.length) return null;
            const data = scoped.map((row) => {
              const annualized = target === 'quarter' && Math.abs(row.value) < 1 ? row.value * 4 : row.value;
              return toPercent(annualized);
            });
            return {
              labels: scoped.map((row) => formatPeriodLabel(row.date)),
              datasets: [{ label: 'ROIC', data, backgroundColor: '#8BC34A' }],
            };
          }

          if (!series.length) return null;
          const getRoicFraction = (km: any): number => {
            const raw = (km as any)?.returnOnInvestedCapital ?? (km as any)?.roic;
            const num = typeof raw === 'number' ? raw : Number(raw);
            return Number.isFinite(num) ? num : 0;
          };

          if (selectedTimeRange === 'TTM') {
            const labels = series.map((km: any) => formatPeriodLabel(km.date)).slice(3);
            const roicTtmSeries: number[] = [];
            for (let i = 3; i < series.length; i++) {
              let nopatSum = 0;
              let investedCapSum = 0;
              let investedCapCount = 0;

              for (let j = i - 3; j <= i; j++) {
                const km = series[j];
                const icRaw = (km as any)?.investedCapital;
                const investedCapital = typeof icRaw === 'number' ? icRaw : Number(icRaw);
                const roicQ = getRoicFraction(km);
                if (!Number.isFinite(investedCapital) || investedCapital <= 0) continue;
                if (!Number.isFinite(roicQ)) continue;
                nopatSum += roicQ * investedCapital;
                investedCapSum += investedCapital;
                investedCapCount++;
              }

              const investedCapAvg = investedCapCount ? investedCapSum / investedCapCount : 0;
              const roicTtm = investedCapAvg > 0 ? nopatSum / investedCapAvg : 0;
              roicTtmSeries.push(toPercent(roicTtm));
            }

            return { labels, datasets: [{ label: 'ROIC', data: roicTtmSeries, backgroundColor: '#8BC34A' }] };
          }

          const labels = series.map((km: any) => formatPeriodLabel(km.date));
          const roicSeries = series.map((km: any) => toPercent(getRoicFraction(km)));
          return { labels, datasets: [{ label: 'ROIC', data: roicSeries, backgroundColor: '#8BC34A' }] };
        })();

        setRoicExpandedData(next);
      } catch (e) {
        console.error('❌ Error loading expanded ROIC history:', e);
      } finally {
        if (!cancelled && roicExpandRequestIdRef.current === requestId) {
          setRoicExpandedLoading(false);
        }
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [expandedChart, selectedTimeRange, selectedStock]);

  // Update chart visibility without refetching data
  const applyVisibility = useMemo(() => {
    return (data: ChartData | null, visibilityByLabel: Record<string, boolean>) => {
      if (!data) return data;
      return {
        ...data,
        datasets: data.datasets.map((ds: any) => {
          const label = ds?.label;
          const isVisible = typeof label === 'string' ? visibilityByLabel[label] : undefined;
          if (typeof isVisible !== 'boolean') return ds;
          return { ...ds, hidden: !isVisible };
        }),
      };
    };
  }, []);

  useEffect(() => {
    setCashDebtData((prev) => applyVisibility(prev, { Cash: cashDebtVisible.cash, Debt: cashDebtVisible.debt }));
  }, [cashDebtVisible, applyVisibility]);

  useEffect(() => {
    setRevenueIncomeData((prev) =>
      applyVisibility(prev, {
        Revenue: revenueIncomeVisible.revenue,
        'Net Income': revenueIncomeVisible.netIncome,
        'Operating Income': revenueIncomeVisible.operatingIncome,
      })
    );
  }, [revenueIncomeVisible, applyVisibility]);

  useEffect(() => {
    setCashFlowData((prev) =>
      applyVisibility(prev, {
        'Operating Cash Flow': cashFlowVisible.operating,
        'Free Cash Flow': cashFlowVisible.free,
      })
    );
  }, [cashFlowVisible, applyVisibility]);

  useEffect(() => {
    setMarginsData((prev) =>
      applyVisibility(prev, {
        'Gross Margin': marginsVisible.gross,
        'Operating Margin': marginsVisible.operating,
        'Net Margin': marginsVisible.net,
        'FCF Margin': marginsVisible.fcf,
      })
    );
  }, [marginsVisible, applyVisibility]);

  const formatPeriodLabel = (dateString: string) => {
    const date = new Date(dateString);
    return `${date.getMonth() + 1}/${date.getFullYear()}`;
  };

  const getPeriod = (timeRange: TimeRange): 'annual' | 'quarter' => (timeRange === 'Annual' ? 'annual' : 'quarter');

  const getLimit = (timeRange: TimeRange): number => {
    // TTM needs enough quarterly points to build a rolling 4-quarter series.
    if (timeRange === 'TTM') return 20;
    // Quarterly: show ~2021+ in the main charts.
    if (timeRange === 'Quarterly') return 20;
    return 10;
  };

  const rollingTTMSum = (values: number[]): number[] => {
    if (!values || values.length < 4) return [];
    const out: number[] = [];
    for (let i = 3; i < values.length; i++) {
      out.push((values[i - 3] || 0) + (values[i - 2] || 0) + (values[i - 1] || 0) + (values[i] || 0));
    }
    return out;
  };

  const rollingTTMSumNullable = (values: Array<number | null>): Array<number | null> => {
    if (!values || values.length < 4) return [];
    const out: Array<number | null> = [];
    for (let i = 3; i < values.length; i++) {
      const window = [values[i - 3], values[i - 2], values[i - 1], values[i]];
      if (window.some((v) => v == null)) {
        out.push(null);
      } else {
        out.push((window[0] as number) + (window[1] as number) + (window[2] as number) + (window[3] as number));
      }
    }
    return out;
  };

  const buildDateKey = (value: unknown): string => {
    const raw = String(value ?? '').trim();
    if (!raw) return '';
    const dt = new Date(raw);
    if (!Number.isFinite(dt.getTime())) return raw;
    return dt.toISOString().slice(0, 10);
  };

  const buildValueMapByDate = (rows: Array<{ date?: string; value?: number }>) => {
    const map = new Map<string, number>();
    rows.forEach((row) => {
      const key = buildDateKey(row?.date);
      const value = toFiniteNumber(row?.value);
      if (key && value != null) map.set(key, value);
    });
    return map;
  };

  const buildRowMapByDate = (rows: any[]) => {
    const map = new Map<string, any>();
    rows.forEach((row) => {
      const key = buildDateKey(row?.date);
      if (key) map.set(key, row);
    });
    return map;
  };

  const getValueWithFallback = (
    row: any,
    fallbackMap: Map<string, any>,
    extractor: (value: any) => number | null
  ): number | null => {
    const value = extractor(row);
    if (value != null) return value;
    const key = buildDateKey(row?.date);
    if (!key) return null;
    const fallback = fallbackMap.get(key);
    return fallback ? extractor(fallback) : null;
  };

  const updateChartSources = (next: Record<string, 'fiscal' | 'fmp'>) => {
    setChartSources((prev) => ({ ...prev, ...next }));
  };

  const fetchAllData = async (requestId: number, baseSnapshot: ChartsSnapshot | null): Promise<ChartsSnapshot> => {
    const isColdLoad = !baseSnapshot;
    let snapshot: ChartsSnapshot = baseSnapshot ?? {
      cashDebtData: null,
      revenueIncomeData: null,
      epsData: null,
      cashFlowData: null,
      marginsData: null,
      roicData: null,
      dividendsData: null,
      sharesData: null,
      peRatioData: null,
      priceFcfData: null,
    };

    const isCurrent = () => requestIdRef.current === requestId;
    const settledValue = <T,>(r: PromiseSettledResult<T>): T | null => (r.status === 'fulfilled' ? r.value : null);

    // Fetch base datasets ONCE (these were previously fetched repeatedly per chart).
    const incomePeriod = getPeriod(selectedTimeRange);
    const cashFlowPeriod = getPeriod(selectedTimeRange);
    const keyMetricsPeriod = selectedTimeRange === 'Annual' ? 'annual' : 'quarter';
    // For TTM view we still use quarterly balance sheet data, but need enough history
    // to match the other TTM charts' visible year range (~2021–2025).
    const balancePeriod = selectedTimeRange === 'TTM' ? 'quarter' : getPeriod(selectedTimeRange);

    const incomeLimit = getLimit(selectedTimeRange);
    const cashFlowLimit = getLimit(selectedTimeRange);
    const keyMetricsLimit = getLimit(selectedTimeRange);
    const balanceLimit = getLimit(selectedTimeRange);

    const balancePromise = fetchBalanceSheet(selectedStock, balancePeriod, balanceLimit);
    const incomePromise = fetchIncomeStatement(selectedStock, incomePeriod, incomeLimit);
    const cashFlowPromise = fetchCashFlowStatement(selectedStock, cashFlowPeriod, cashFlowLimit);
    const keyMetricsPromise = fetchKeyMetrics(selectedStock, keyMetricsPeriod, keyMetricsLimit);

    const fiscalMetricsPromise = fetchFiscalStandardizedMetricsList();
    const fiscalIncomePromise = fiscalMetricsPromise.then((catalog) =>
      fetchFiscalStandardizedIncomeStatement(selectedStock, catalog)
    );
    const fiscalCashPromise = fiscalMetricsPromise.then((catalog) =>
      fetchFiscalStandardizedCashFlowStatement(selectedStock, catalog)
    );
    const fiscalBalancePromise = fiscalMetricsPromise.then((catalog) =>
      fetchFiscalStandardizedBalanceSheet(selectedStock, catalog)
    );
    const fiscalRoicPromise = fetchFiscalRoicRatios(selectedStock);
    const fiscalMarketCapPromise = fetchFiscalMarketCapRatios(selectedStock);

    let balanceSheets: any[] = [];
    let incomeStatements: any[] = [];
    let cashFlowStatements: any[] = [];
    let keyMetrics: any[] = [];
    let fiscalBalanceRows: any[] = [];
    let fiscalIncomeRows: any[] = [];
    let fiscalCashRows: any[] = [];
    let fiscalRoicRows: Array<{ date: string; periodType: string; value?: number }> = [];
    let fiscalMarketCapRows: Array<{ date: string; periodType: string; value?: number }> = [];
    let fiscalIsAvailable = fiscalAvailable;

    const resolveFiscalAvailability = (
      income: { annual: any[]; quarter: any[]; error?: string } | null,
      cash: { annual: any[]; quarter: any[]; error?: string } | null,
      balance: { annual: any[]; quarter: any[]; error?: string } | null
    ) => {
      const fiscalCount =
        (income?.annual?.length || 0) +
        (income?.quarter?.length || 0) +
        (cash?.annual?.length || 0) +
        (cash?.quarter?.length || 0) +
        (balance?.annual?.length || 0) +
        (balance?.quarter?.length || 0);
      const errorText = String(income?.error || cash?.error || balance?.error || '').trim();
      const unavailableByMessage =
        Boolean(errorText) && /data is not yet available|companies-list|companies list/i.test(errorText);
      return !unavailableByMessage && fiscalCount > 0;
    };

    const getCashValue = (row: any): number | null => {
      const cashAndShortTerm = toFiniteNumber(row?.cashAndShortTermInvestments);
      if (cashAndShortTerm != null) return cashAndShortTerm;
      const cash = toFiniteNumber(row?.cashAndCashEquivalents);
      const shortTerm = toFiniteNumber(row?.shortTermInvestments);
      if (cash != null || shortTerm != null) return (cash ?? 0) + (shortTerm ?? 0);
      return null;
    };

    const getDebtValue = (row: any): number | null => toFiniteNumber(row?.longTermDebt);

    const getRevenueValue = (row: any): number | null => toFiniteNumber(row?.revenue);
    const getNetIncomeValue = (row: any): number | null => toFiniteNumber(row?.netIncome);
    const getOperatingIncomeValue = (row: any): number | null => toFiniteNumber(row?.operatingIncome);
    const getGrossProfitValue = (row: any): number | null => toFiniteNumber(row?.grossProfit);
    const getEpsValue = (row: any): number | null => toFiniteNumber(row?.epsDiluted);
    const getSharesValue = (row: any): number | null =>
      toFiniteNumber(row?.weightedAverageShsOutDil ?? row?.dilutedWeightedAverageSharesOutstanding);

    const getOcfValue = (row: any): number | null => {
      const ocf = toFiniteNumber(row?.operatingCashFlow);
      if (ocf != null) return ocf;
      return toFiniteNumber(row?.netCashProvidedByOperatingActivities);
    };

    const getCapexValue = (row: any): number | null => {
      const capex = toFiniteNumber(row?.capitalExpenditure);
      if (capex != null) return capex;
      return toFiniteNumber(row?.investmentsInPropertyPlantAndEquipment);
    };

    const getFcfValue = (row: any): number | null => {
      const fcf = toFiniteNumber(row?.freeCashFlow);
      if (fcf != null) return fcf;
      const ocf = getOcfValue(row);
      const capex = getCapexValue(row);
      if (ocf == null || capex == null) return null;
      return ocf + capex;
    };

    const getDividendValue = (row: any): number | null => {
      const common = toFiniteNumber((row as any)?.commonDividendsPaid);
      if (common != null) return common;
      const net = toFiniteNumber((row as any)?.netDividendsPaid);
      if (net != null) return net;
      return toFiniteNumber(row?.dividendsPaid);
    };

    const chartSourceUpdates: Record<string, 'fiscal' | 'fmp'> = {};

    const trimFiscalSeries = <T extends { date?: unknown }>(rows: T[]): T[] => {
      if (selectedTimeRange !== 'Quarterly' && selectedTimeRange !== 'TTM') return rows;
      const limit = getLimit(selectedTimeRange);
      if (!Number.isFinite(limit) || limit <= 0) return rows;
      const sorted = [...rows].sort((a, b) => {
        const at = new Date(a?.date as any).getTime();
        const bt = new Date(b?.date as any).getTime();
        if (!Number.isFinite(at) && !Number.isFinite(bt)) return 0;
        if (!Number.isFinite(at)) return 1;
        if (!Number.isFinite(bt)) return -1;
        return at - bt;
      });
      return sorted.length > limit ? sorted.slice(-limit) : sorted;
    };

    const getFmpMinDate = (rows: any[]): Date | null => {
      let minTime: number | null = null;
      for (const row of rows) {
        const d = new Date(row?.date);
        const t = d.getTime();
        if (!Number.isFinite(t)) continue;
        if (minTime == null || t < minTime) minTime = t;
      }
      return minTime == null ? null : new Date(minTime);
    };

    const resolveFiscalCutoff = (fmpRows: any[], fallback: Date): Date => {
      return getFmpMinDate(fmpRows) ?? fallback;
    };

    const buildCashDebt = (): ChartData | null => {
      const useFiscal = fiscalIsAvailable;
      const rows = useFiscal ? fiscalBalanceRows : balanceSheets;
      chartSourceUpdates['cash-debt-chart'] = useFiscal ? 'fiscal' : 'fmp';
      if (!rows || rows.length === 0) return null;
      const fmpBalanceMap = useFiscal ? buildRowMapByDate(balanceSheets) : new Map<string, any>();
      const reversedData = [...rows].reverse();

      const filteredData = (() => {
        const startFiscal = resolveFiscalCutoff(balanceSheets, new Date('2011-01-01'));
        if (useFiscal) {
          const filtered = reversedData.filter((bs: any) => {
            const d = new Date(bs?.date);
            return Number.isFinite(d.getTime()) && d >= startFiscal;
          });
          return trimFiscalSeries(filtered);
        }

        if (selectedTimeRange !== 'TTM') return reversedData;
        // Keep the same visible year window as the other TTM charts.
        const start = new Date('2021-01-01');
        const end = new Date('2025-12-31');
        return reversedData.filter((bs: any) => {
          const d = new Date(bs?.date);
          return Number.isFinite(d.getTime()) && d >= start && d <= end;
        });
      })();

      const labels = filteredData.map((bs: any) => formatPeriodLabel(bs.date));
      const cashData = filteredData.map((bs: any) => {
        const value = useFiscal ? getValueWithFallback(bs, fmpBalanceMap, getCashValue) : getCashValue(bs);
        return value == null ? null : value / 1000000;
      });
      const longTermDebtData = filteredData.map((bs: any) => {
        const value = useFiscal ? getValueWithFallback(bs, fmpBalanceMap, getDebtValue) : getDebtValue(bs);
        return value == null ? null : value / 1000000;
      });
      return {
        labels,
        datasets: [
          { label: 'Cash', data: cashData, backgroundColor: '#07DBFA', hidden: !cashDebtVisible.cash },
          { label: 'Debt', data: longTermDebtData, backgroundColor: '#FF9800', hidden: !cashDebtVisible.debt },
        ],
      };
    };

    const buildRevenueIncome = (): ChartData | null => {
      const useFiscal = fiscalIsAvailable;
      const rows = useFiscal ? fiscalIncomeRows : incomeStatements;
      chartSourceUpdates['revenue-income-chart'] = useFiscal ? 'fiscal' : 'fmp';
      if (!rows || rows.length === 0) return null;
      const fmpIncomeMap = useFiscal ? buildRowMapByDate(incomeStatements) : new Map<string, any>();
      const reversedData = (() => {
        const base = [...rows].reverse();
        if (!useFiscal) return base;
        const startFiscal = resolveFiscalCutoff(incomeStatements, new Date('2011-01-01'));
        const filtered = base.filter((is: any) => {
          const d = new Date(is?.date);
          return Number.isFinite(d.getTime()) && d >= startFiscal;
        });
        return trimFiscalSeries(filtered);
      })();
      if (selectedTimeRange === 'TTM') {
        const revenueQ = reversedData.map((is: any) => {
          const value = useFiscal ? getValueWithFallback(is, fmpIncomeMap, getRevenueValue) : getRevenueValue(is);
          return value == null ? null : value / 1000000;
        });
        const netIncomeQ = reversedData.map((is: any) => {
          const value = useFiscal ? getValueWithFallback(is, fmpIncomeMap, getNetIncomeValue) : getNetIncomeValue(is);
          return value == null ? null : value / 1000000;
        });
        const operatingIncomeQ = reversedData.map((is: any) => {
          const value = useFiscal ? getValueWithFallback(is, fmpIncomeMap, getOperatingIncomeValue) : getOperatingIncomeValue(is);
          return value == null ? null : value / 1000000;
        });
        return {
          labels: reversedData.map((is: any) => formatPeriodLabel(is.date)).slice(3),
          datasets: [
            { label: 'Revenue', data: rollingTTMSumNullable(revenueQ), backgroundColor: '#1F94FF', hidden: !revenueIncomeVisible.revenue },
            { label: 'Operating Income', data: rollingTTMSumNullable(operatingIncomeQ), backgroundColor: '#07DBFA', hidden: !revenueIncomeVisible.operatingIncome },
            { label: 'Net Income', data: rollingTTMSumNullable(netIncomeQ), backgroundColor: '#988AFC', hidden: !revenueIncomeVisible.netIncome },
          ],
        };
      }

      return {
        labels: reversedData.map((is: any) => formatPeriodLabel(is.date)),
        datasets: [
          {
            label: 'Revenue',
            data: reversedData.map((is: any) => {
              const value = useFiscal ? getValueWithFallback(is, fmpIncomeMap, getRevenueValue) : getRevenueValue(is);
              return value == null ? null : value / 1000000;
            }),
            backgroundColor: '#1F94FF',
            hidden: !revenueIncomeVisible.revenue,
          },
          {
            label: 'Operating Income',
            data: reversedData.map((is: any) => {
              const value = useFiscal ? getValueWithFallback(is, fmpIncomeMap, getOperatingIncomeValue) : getOperatingIncomeValue(is);
              return value == null ? null : value / 1000000;
            }),
            backgroundColor: '#07DBFA',
            hidden: !revenueIncomeVisible.operatingIncome,
          },
          {
            label: 'Net Income',
            data: reversedData.map((is: any) => {
              const value = useFiscal ? getValueWithFallback(is, fmpIncomeMap, getNetIncomeValue) : getNetIncomeValue(is);
              return value == null ? null : value / 1000000;
            }),
            backgroundColor: '#988AFC',
            hidden: !revenueIncomeVisible.netIncome,
          },
        ],
      };
    };

    const buildEPS = (): ChartData | null => {
      const useFiscal = fiscalIsAvailable;
      const rows = useFiscal ? fiscalIncomeRows : incomeStatements;
      chartSourceUpdates['eps-chart'] = useFiscal ? 'fiscal' : 'fmp';
      if (!rows || rows.length === 0) return null;
      const fmpIncomeMap = useFiscal ? buildRowMapByDate(incomeStatements) : new Map<string, any>();
      const reversedData = (() => {
        const base = [...rows].reverse();
        if (!useFiscal) return base;
        const startFiscal = resolveFiscalCutoff(incomeStatements, new Date('2011-01-01'));
        const filtered = base.filter((is: any) => {
          const d = new Date(is?.date);
          return Number.isFinite(d.getTime()) && d >= startFiscal;
        });
        return trimFiscalSeries(filtered);
      })();
      if (selectedTimeRange === 'TTM') {
        const epsQ = reversedData.map((is: any) => {
          return useFiscal ? getValueWithFallback(is, fmpIncomeMap, getEpsValue) : getEpsValue(is);
        });
        return {
          labels: reversedData.map((is: any) => formatPeriodLabel(is.date)).slice(3),
          datasets: [{ label: 'EPS', data: rollingTTMSumNullable(epsQ), backgroundColor: '#FF8563', borderColor: '#FF8563', fill: false }],
        };
      }
      return {
        labels: reversedData.map((is: any) => formatPeriodLabel(is.date)),
        datasets: [{
          label: 'EPS',
          data: reversedData.map((is: any) => {
            const value = useFiscal ? getValueWithFallback(is, fmpIncomeMap, getEpsValue) : getEpsValue(is);
            return value == null ? null : value;
          }),
          backgroundColor: '#FF8563',
          borderColor: '#FF8563',
          fill: false,
        }],
      };
    };

    const buildCashFlow = (): ChartData | null => {
      const useFiscal = fiscalIsAvailable;
      const rows = useFiscal ? fiscalCashRows : cashFlowStatements;
      chartSourceUpdates['cash-flow-chart'] = useFiscal ? 'fiscal' : 'fmp';
      if (!rows || rows.length === 0) return null;
      const fmpCashMap = useFiscal ? buildRowMapByDate(cashFlowStatements) : new Map<string, any>();
      const reversedData = (() => {
        const base = [...rows].reverse();
        if (!useFiscal) return base;
        const startFiscal = resolveFiscalCutoff(cashFlowStatements, new Date('2011-01-01'));
        const filtered = base.filter((cf: any) => {
          const d = new Date(cf?.date);
          return Number.isFinite(d.getTime()) && d >= startFiscal;
        });
        return trimFiscalSeries(filtered);
      })();
      if (selectedTimeRange === 'TTM') {
        const ocfQ = reversedData.map((cf: any) => {
          const value = useFiscal ? getValueWithFallback(cf, fmpCashMap, getOcfValue) : getOcfValue(cf);
          return value == null ? null : value / 1000000;
        });
        const fcfQ = reversedData.map((cf: any) => {
          const value = useFiscal ? getValueWithFallback(cf, fmpCashMap, getFcfValue) : getFcfValue(cf);
          return value == null ? null : value / 1000000;
        });
        return {
          labels: reversedData.map((cf: any) => formatPeriodLabel(cf.date)).slice(3),
          datasets: [
            { label: 'Operating Cash Flow', data: rollingTTMSumNullable(ocfQ), backgroundColor: '#07DBFA', hidden: !cashFlowVisible.operating },
            { label: 'Free Cash Flow', data: rollingTTMSumNullable(fcfQ), backgroundColor: '#009688', hidden: !cashFlowVisible.free },
          ],
        };
      }
      return {
        labels: reversedData.map((cf: any) => formatPeriodLabel(cf.date)),
        datasets: [
          {
            label: 'Operating Cash Flow',
            data: reversedData.map((cf: any) => {
              const value = useFiscal ? getValueWithFallback(cf, fmpCashMap, getOcfValue) : getOcfValue(cf);
              return value == null ? null : value / 1000000;
            }),
            backgroundColor: '#07DBFA',
            hidden: !cashFlowVisible.operating,
          },
          {
            label: 'Free Cash Flow',
            data: reversedData.map((cf: any) => {
              const value = useFiscal ? getValueWithFallback(cf, fmpCashMap, getFcfValue) : getFcfValue(cf);
              return value == null ? null : value / 1000000;
            }),
            backgroundColor: '#009688',
            hidden: !cashFlowVisible.free,
          },
        ],
      };
    };

    const buildMargins = (): ChartData | null => {
      const useFiscal = fiscalIsAvailable;
      const primaryIncome = useFiscal ? fiscalIncomeRows : incomeStatements;
      const primaryCash = useFiscal ? fiscalCashRows : cashFlowStatements;
      chartSourceUpdates['margins-chart'] = useFiscal ? 'fiscal' : 'fmp';
      if (!primaryIncome || primaryIncome.length === 0) return null;
      const fmpIncomeMap = useFiscal ? buildRowMapByDate(incomeStatements) : new Map<string, any>();
      const fmpCashMap = useFiscal ? buildRowMapByDate(cashFlowStatements) : new Map<string, any>();
      const reversedIncome = (() => {
        const base = [...primaryIncome].reverse();
        if (!useFiscal) return base;
        const startFiscal = resolveFiscalCutoff(incomeStatements, new Date('2011-01-01'));
        const filtered = base.filter((is: any) => {
          const d = new Date(is?.date);
          return Number.isFinite(d.getTime()) && d >= startFiscal;
        });
        return trimFiscalSeries(filtered);
      })();
      const reversedCashFlow = (() => {
        const base = primaryCash ? [...primaryCash].reverse() : [];
        if (!useFiscal) return base;
        const startFiscal = resolveFiscalCutoff(cashFlowStatements, new Date('2011-01-01'));
        const filtered = base.filter((cf: any) => {
          const d = new Date(cf?.date);
          return Number.isFinite(d.getTime()) && d >= startFiscal;
        });
        return trimFiscalSeries(filtered);
      })();
      const alignedLength = Math.min(reversedIncome.length, reversedCashFlow.length || reversedIncome.length);
      const incomeAligned = reversedIncome.slice(0, alignedLength);
      const cashFlowAligned = reversedCashFlow.slice(0, alignedLength);

      if (selectedTimeRange === 'TTM') {
        const revenueQ = incomeAligned.map((is: any) => (
          useFiscal ? getValueWithFallback(is, fmpIncomeMap, getRevenueValue) : getRevenueValue(is)
        ));
        const grossProfitQ = incomeAligned.map((is: any) => (
          useFiscal ? getValueWithFallback(is, fmpIncomeMap, getGrossProfitValue) : getGrossProfitValue(is)
        ));
        const operatingIncomeQ = incomeAligned.map((is: any) => (
          useFiscal ? getValueWithFallback(is, fmpIncomeMap, getOperatingIncomeValue) : getOperatingIncomeValue(is)
        ));
        const netIncomeQ = incomeAligned.map((is: any) => (
          useFiscal ? getValueWithFallback(is, fmpIncomeMap, getNetIncomeValue) : getNetIncomeValue(is)
        ));
        const fcfQ = cashFlowAligned.length
          ? cashFlowAligned.map((cf: any) => (
              useFiscal ? getValueWithFallback(cf, fmpCashMap, getFcfValue) : getFcfValue(cf)
            ))
          : incomeAligned.map(() => null);

        const revenueTTM = rollingTTMSumNullable(revenueQ);
        const grossProfitTTM = rollingTTMSumNullable(grossProfitQ);
        const operatingIncomeTTM = rollingTTMSumNullable(operatingIncomeQ);
        const netIncomeTTM = rollingTTMSumNullable(netIncomeQ);
        const fcfTTM = rollingTTMSumNullable(fcfQ);

        const labels = incomeAligned.map((is: any) => formatPeriodLabel(is.date)).slice(3);
        const grossMarginData = revenueTTM.map((rev, idx) => {
          const gp = grossProfitTTM[idx];
          if (rev == null || gp == null || rev <= 0) return null;
          return (gp / rev) * 100;
        });
        const operatingMarginData = revenueTTM.map((rev, idx) => {
          const op = operatingIncomeTTM[idx];
          if (rev == null || op == null || rev <= 0) return null;
          return (op / rev) * 100;
        });
        const netMarginData = revenueTTM.map((rev, idx) => {
          const ni = netIncomeTTM[idx];
          if (rev == null || ni == null || rev <= 0) return null;
          return (ni / rev) * 100;
        });
        const fcfMarginData = revenueTTM.map((rev, idx) => {
          const fcf = fcfTTM[idx];
          if (rev == null || fcf == null || rev <= 0) return null;
          return (fcf / rev) * 100;
        });

        return {
          labels,
          datasets: [
            { label: 'Gross Margin', data: grossMarginData, backgroundColor: '#9575CD', hidden: !marginsVisible.gross },
            { label: 'Operating Margin', data: operatingMarginData, backgroundColor: '#8BC34A', hidden: !marginsVisible.operating },
            { label: 'Net Margin', data: netMarginData, backgroundColor: '#FF9800', hidden: !marginsVisible.net },
            { label: 'FCF Margin', data: fcfMarginData, backgroundColor: '#07DBFA', hidden: !marginsVisible.fcf },
          ],
        };
      }

      const labels = incomeAligned.map((is: any) => formatPeriodLabel(is.date));
      const grossMarginData = incomeAligned.map((is: any) => {
        const revenue = useFiscal ? getValueWithFallback(is, fmpIncomeMap, getRevenueValue) : getRevenueValue(is);
        const grossProfit = useFiscal ? getValueWithFallback(is, fmpIncomeMap, getGrossProfitValue) : getGrossProfitValue(is);
        if (revenue == null || grossProfit == null || revenue <= 0) return null;
        return (grossProfit / revenue) * 100;
      });
      const operatingMarginData = incomeAligned.map((is: any) => {
        const revenue = useFiscal ? getValueWithFallback(is, fmpIncomeMap, getRevenueValue) : getRevenueValue(is);
        const operatingIncome = useFiscal ? getValueWithFallback(is, fmpIncomeMap, getOperatingIncomeValue) : getOperatingIncomeValue(is);
        if (revenue == null || operatingIncome == null || revenue <= 0) return null;
        return (operatingIncome / revenue) * 100;
      });
      const netMarginData = incomeAligned.map((is: any) => {
        const revenue = useFiscal ? getValueWithFallback(is, fmpIncomeMap, getRevenueValue) : getRevenueValue(is);
        const netIncome = useFiscal ? getValueWithFallback(is, fmpIncomeMap, getNetIncomeValue) : getNetIncomeValue(is);
        if (revenue == null || netIncome == null || revenue <= 0) return null;
        return (netIncome / revenue) * 100;
      });
      const fcfMarginData = incomeAligned.map((is: any, index: number) => {
        const cf = cashFlowAligned[index];
        const revenue = useFiscal ? getValueWithFallback(is, fmpIncomeMap, getRevenueValue) : getRevenueValue(is);
        if (!cf || revenue == null || revenue <= 0) return null;
        const fcf = useFiscal ? getValueWithFallback(cf, fmpCashMap, getFcfValue) : getFcfValue(cf);
        if (fcf == null) return null;
        return (fcf / revenue) * 100;
      });

      return {
        labels,
        datasets: [
          { label: 'Gross Margin', data: grossMarginData, backgroundColor: '#9575CD', hidden: !marginsVisible.gross },
          { label: 'Operating Margin', data: operatingMarginData, backgroundColor: '#8BC34A', hidden: !marginsVisible.operating },
          { label: 'Net Margin', data: netMarginData, backgroundColor: '#FF9800', hidden: !marginsVisible.net },
          { label: 'FCF Margin', data: fcfMarginData, backgroundColor: '#07DBFA', hidden: !marginsVisible.fcf },
        ],
      };
    };

    const buildROIC = (): ChartData | null => {
      if (fiscalIsAvailable) {
        chartSourceUpdates['roic-chart'] = 'fiscal';
        if (!fiscalRoicRows.length) return null;
        const roicFallbackMap = buildValueMapByDate(
          (keyMetrics ?? []).map((km: any) => ({
            date: km?.date,
            value: toFiniteNumber((km as any)?.returnOnInvestedCapital ?? (km as any)?.roic) ?? undefined,
          }))
        );
        const normalized = fiscalRoicRows
          .filter((row) => row && row.date)
          .map((row) => ({
            ...row,
            date: String(row.date),
            value: toFiniteNumber(row.value),
          }))
          .filter((row) => {
            const cutoff = resolveFiscalCutoff(keyMetrics, new Date('2016-01-01'));
            const d = new Date(row.date);
            return Number.isFinite(d.getTime()) && d >= cutoff;
          })
          .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

        if (selectedTimeRange === 'TTM') {
          const series = trimFiscalSeries(
            normalized.filter((row) => String(row.periodType).toLowerCase().includes('ltm'))
          );
          if (!series.length) return null;
          const labels = series.map((row) => formatPeriodLabel(row.date));
          const data = series.map((row) => {
            const fallback = roicFallbackMap.get(buildDateKey(row.date));
            const raw = row.value ?? fallback;
            if (raw == null) return null;
            const toPercent = (value: number) => (Math.abs(value) < 1 ? value * 100 : value);
            return toPercent(raw);
          });
          return { labels, datasets: [{ label: 'ROIC', data, backgroundColor: '#8BC34A' }] };
        }

        const target = selectedTimeRange === 'Annual' ? 'annual' : 'quarter';
        const series = trimFiscalSeries(
          normalized.filter((row) => String(row.periodType).toLowerCase().includes(target))
        );
        if (!series.length) return null;
        const labels = series.map((row) => formatPeriodLabel(row.date));
        const data = series.map((row) => {
          const fallback = roicFallbackMap.get(buildDateKey(row.date));
          const raw = row.value ?? fallback;
          if (raw == null) return null;
          const annualized = target === 'quarter' && Math.abs(raw) < 1 ? raw * 4 : raw;
          const toPercent = (value: number) => (Math.abs(value) < 1 ? value * 100 : value);
          return toPercent(annualized);
        });
        return { labels, datasets: [{ label: 'ROIC', data, backgroundColor: '#8BC34A' }] };
      }

      chartSourceUpdates['roic-chart'] = 'fmp';
      if (!keyMetrics || keyMetrics.length === 0) return null;
      const reversedData = [...keyMetrics].reverse();
      const toPercent = (value: number) => {
        if (!Number.isFinite(value) || value === 0) return 0;
        return Math.abs(value) < 1 ? value * 100 : value;
      };
      const getRoicFraction = (km: any): number => {
        const raw = (km as any)?.returnOnInvestedCapital ?? (km as any)?.roic;
        const num = typeof raw === 'number' ? raw : Number(raw);
        return Number.isFinite(num) ? num : 0;
      };

      // Quarterly ROIC from the API is a per-quarter ratio (~6% for MSFT).
      // We annualize it (x4) for Quarterly view. For TTM we compute TTM NOPAT / avg invested capital.
      if (selectedTimeRange === 'TTM') {
        const labels = reversedData.map((km: any) => formatPeriodLabel(km.date)).slice(3);
        const roicTtmSeries: number[] = [];
        for (let i = 3; i < reversedData.length; i++) {
          let nopatSum = 0;
          let investedCapSum = 0;
          let investedCapCount = 0;

          for (let j = i - 3; j <= i; j++) {
            const km = reversedData[j];
            const icRaw = (km as any)?.investedCapital;
            const investedCapital = typeof icRaw === 'number' ? icRaw : Number(icRaw);
            const roicQ = getRoicFraction(km);
            if (!Number.isFinite(investedCapital) || investedCapital <= 0) continue;
            if (!Number.isFinite(roicQ)) continue;
            nopatSum += roicQ * investedCapital;
            investedCapSum += investedCapital;
            investedCapCount++;
          }

          const investedCapAvg = investedCapCount ? investedCapSum / investedCapCount : 0;
          const roicTtm = investedCapAvg > 0 ? nopatSum / investedCapAvg : 0;
          roicTtmSeries.push(toPercent(roicTtm));
        }

        return { labels, datasets: [{ label: 'ROIC', data: roicTtmSeries, backgroundColor: '#8BC34A' }] };
      }

      const labels = reversedData.map((km: any) => formatPeriodLabel(km.date));
      const roicData = reversedData.map((km: any) => {
        const roicQ = getRoicFraction(km);
        if (!roicQ) return 0;

        if (selectedTimeRange === 'Quarterly') {
          // Annualize quarterly ratio if it's in fractional form.
          const annualized = Math.abs(roicQ) < 1 ? roicQ * 4 : roicQ;
          return toPercent(annualized);
        }

        // Annual
        return toPercent(roicQ);
      });

      return { labels, datasets: [{ label: 'ROIC', data: roicData, backgroundColor: '#8BC34A' }] };
    };

    const buildDividends = (): ChartData | null => {
      const useFiscal = fiscalIsAvailable;
      const rows = useFiscal ? fiscalCashRows : cashFlowStatements;
      chartSourceUpdates['dividends-chart'] = useFiscal ? 'fiscal' : 'fmp';
      if (!rows || rows.length === 0) return null;
      const fmpCashMap = useFiscal ? buildRowMapByDate(cashFlowStatements) : new Map<string, any>();
      const reversedData = (() => {
        const base = [...rows].reverse();
        if (!useFiscal) return base;
        const startFiscal = new Date('2011-01-01');
        const filtered = base.filter((cf: any) => {
          const d = new Date(cf?.date);
          return Number.isFinite(d.getTime()) && d >= startFiscal;
        });
        return trimFiscalSeries(filtered);
      })();
      if (selectedTimeRange === 'TTM') {
        const dividendsQ = reversedData.map((cf: any) => {
          const value = useFiscal ? getValueWithFallback(cf, fmpCashMap, getDividendValue) : getDividendValue(cf);
          return value == null ? null : Math.abs(value);
        });
        return {
          labels: reversedData.map((cf: any) => formatPeriodLabel(cf.date)).slice(3),
          datasets: [{
            label: 'Dividends',
            data: rollingTTMSumNullable(dividendsQ).map((v) => (v == null ? null : v / 1000000)),
            backgroundColor: '#DCE775',
          }],
        };
      }
      return {
        labels: reversedData.map((cf: any) => formatPeriodLabel(cf.date)),
        datasets: [
          {
            label: 'Dividends',
            data: reversedData.map((cf: any) => {
              const value = useFiscal ? getValueWithFallback(cf, fmpCashMap, getDividendValue) : getDividendValue(cf);
              return value == null ? null : Math.abs(value) / 1000000;
            }),
            backgroundColor: '#DCE775',
          },
        ],
      };
    };

    const buildShares = (): ChartData | null => {
      const useFiscal = fiscalIsAvailable;
      const rows = useFiscal ? fiscalIncomeRows : incomeStatements;
      chartSourceUpdates['shares-chart'] = useFiscal ? 'fiscal' : 'fmp';
      if (!rows || rows.length === 0) return null;
      const fmpIncomeMap = useFiscal ? buildRowMapByDate(incomeStatements) : new Map<string, any>();
      const reversedData = (() => {
        const base = [...rows].reverse();
        if (!useFiscal) return base;
        const startFiscal = new Date('2016-01-01');
        const filtered = base.filter((is: any) => {
          const d = new Date(is?.date);
          return Number.isFinite(d.getTime()) && d >= startFiscal;
        });
        return trimFiscalSeries(filtered);
      })();
      return {
        labels: reversedData.map((is: any) => formatPeriodLabel(is.date)),
        datasets: [
          {
            label: 'Weighted Avg Shares (Diluted)',
            data: reversedData.map((is: any) => {
              const value = useFiscal ? getValueWithFallback(is, fmpIncomeMap, getSharesValue) : getSharesValue(is);
              return value == null ? null : value / 1000000000;
            }),
            backgroundColor: '#E91E63',
          },
        ],
      };
    };

    const buildPERatio = (): ChartData | null => {
      if (fiscalIsAvailable) {
        chartSourceUpdates['pe-ratio-chart'] = 'fiscal';
        const incomePrimary = fiscalIncomeRows;
        if (!incomePrimary || incomePrimary.length === 0) return null;
        const fmpIncomeMap = buildRowMapByDate(incomeStatements);
        const marketCapFallbackMap = buildValueMapByDate(
          (keyMetrics ?? []).map((km: any) => ({
            date: km?.date,
            value: toFiniteNumber(km?.marketCap) ?? undefined,
          }))
        );

        const periodKey = selectedTimeRange === 'Annual' ? 'annual' : selectedTimeRange === 'Quarterly' ? 'quarter' : 'ltm';
        const marketCapSeries = fiscalMarketCapRows.filter((row) => row.periodType === periodKey);
        const marketCapMap = buildValueMapByDate(marketCapSeries);

        const reversedIncome = (() => {
          const base = [...incomePrimary].reverse();
          const startFiscal = resolveFiscalCutoff(incomeStatements, new Date('2016-01-01'));
          const filtered = base.filter((is: any) => {
            const d = new Date(is?.date);
            return Number.isFinite(d.getTime()) && d >= startFiscal;
          });
          return trimFiscalSeries(filtered);
        })();
        const labels = reversedIncome.map((is: any) => formatPeriodLabel(is.date));
        const marketCaps = reversedIncome.map((is: any) => {
          const key = buildDateKey(is?.date);
          if (!key) return null;
          return marketCapMap.get(key) ?? marketCapFallbackMap.get(key) ?? null;
        });

        if (selectedTimeRange === 'TTM') {
          const netIncomeQ = reversedIncome.map((is: any) => (
            getValueWithFallback(is, fmpIncomeMap, getNetIncomeValue)
          ));
          const netIncomeTTM = rollingTTMSumNullable(netIncomeQ);
          const ttmLabels = labels.slice(3);
          const peRatioData = netIncomeTTM.map((ttmNI, idx) => {
            const marketCap = marketCaps[idx + 3];
            if (marketCap == null || ttmNI == null || ttmNI <= 0) return null;
            return marketCap / ttmNI;
          });
          return { labels: ttmLabels, datasets: [{ label: 'PE Ratio', data: peRatioData, backgroundColor: 'rgba(47, 88, 242, 0.2)', borderColor: '#2F58F2', fill: true }] };
        }

        const peRatioData = reversedIncome.map((is: any, index: number) => {
          const marketCap = marketCaps[index];
          const netIncome = getValueWithFallback(is, fmpIncomeMap, getNetIncomeValue);
          if (marketCap == null || netIncome == null || netIncome <= 0) return null;
          return marketCap / netIncome;
        });

        return { labels, datasets: [{ label: 'PE Ratio', data: peRatioData, backgroundColor: 'rgba(47, 88, 242, 0.2)', borderColor: '#2F58F2', fill: true }] };
      }

      const incomePrimary = incomeStatements;
      chartSourceUpdates['pe-ratio-chart'] = 'fmp';
      if (!keyMetrics || keyMetrics.length === 0 || !incomePrimary || incomePrimary.length === 0) return null;
      const reversedMetrics = [...keyMetrics].reverse();
      const reversedIncome = [...incomePrimary].reverse();
      const alignedLength = Math.min(reversedMetrics.length, reversedIncome.length);
      const metricsAligned = reversedMetrics.slice(0, alignedLength);
      const incomeAligned = reversedIncome.slice(0, alignedLength);

      if (selectedTimeRange === 'TTM') {
        const marketCapQ = metricsAligned.map((km: any) => km.marketCap || 0);
        const netIncomeQ = incomeAligned.map((is: any) => getNetIncomeValue(is));
        const netIncomeTTM = rollingTTMSumNullable(netIncomeQ);
        const labels = incomeAligned.map((is: any) => formatPeriodLabel(is.date)).slice(3);
        const peRatioData = netIncomeTTM.map((ttmNI, idx) => {
          const marketCap = marketCapQ[idx + 3] || 0;
          if (!marketCap || ttmNI == null || ttmNI <= 0) return null;
          return marketCap / ttmNI;
        });
        return { labels, datasets: [{ label: 'PE Ratio', data: peRatioData, backgroundColor: 'rgba(47, 88, 242, 0.2)', borderColor: '#2F58F2', fill: true }] };
      }

      const labels = metricsAligned.map((km: any) => formatPeriodLabel(km.date));
      const peRatioData = metricsAligned.map((km: any, index: number) => {
        const marketCap = km.marketCap;
        const netIncome = getNetIncomeValue(incomeAligned[index]);
        if (!marketCap || netIncome == null || netIncome <= 0) return null;
        return marketCap / netIncome;
      });
      return { labels, datasets: [{ label: 'PE Ratio', data: peRatioData, backgroundColor: 'rgba(47, 88, 242, 0.2)', borderColor: '#2F58F2', fill: true }] };
    };

    const buildPriceFCF = (): ChartData | null => {
      if (fiscalIsAvailable) {
        chartSourceUpdates['price-fcf-chart'] = 'fiscal';
        const cashPrimary = fiscalCashRows;
        if (!cashPrimary || cashPrimary.length === 0) return null;
        const fmpCashMap = buildRowMapByDate(cashFlowStatements);
        const marketCapFallbackMap = buildValueMapByDate(
          (keyMetrics ?? []).map((km: any) => ({
            date: km?.date,
            value: toFiniteNumber(km?.marketCap) ?? undefined,
          }))
        );

        const periodKey = selectedTimeRange === 'Annual' ? 'annual' : selectedTimeRange === 'Quarterly' ? 'quarter' : 'ltm';
        const marketCapSeries = fiscalMarketCapRows.filter((row) => row.periodType === periodKey);
        const marketCapMap = buildValueMapByDate(marketCapSeries);

        const startFiscal = resolveFiscalCutoff(cashFlowStatements, new Date('2016-01-01'));
        const reversedCF = trimFiscalSeries(
          [...cashPrimary].reverse().filter((cf: any) => {
            const d = new Date(cf?.date);
            return Number.isFinite(d.getTime()) && d >= startFiscal;
          })
        );
        const labels = reversedCF.map((cf: any) => formatPeriodLabel(cf.date));
        const marketCaps = reversedCF.map((cf: any) => {
          const key = buildDateKey(cf?.date);
          if (!key) return null;
          return marketCapMap.get(key) ?? marketCapFallbackMap.get(key) ?? null;
        });

        if (selectedTimeRange === 'TTM') {
          const fcfQ = reversedCF.map((cf: any) => (
            getValueWithFallback(cf, fmpCashMap, getFcfValue)
          ));
          const fcfTTM = rollingTTMSumNullable(fcfQ);
          const ttmLabels = labels.slice(3);
          const priceFcfData = fcfTTM.map((ttmFcf, idx) => {
            const marketCap = marketCaps[idx + 3];
            if (marketCap == null || ttmFcf == null || ttmFcf <= 0) return null;
            return marketCap / ttmFcf;
          });
          return { labels: ttmLabels, datasets: [{ label: 'Price / FCF', data: priceFcfData, backgroundColor: '#BA68C8' }] };
        }

        const priceFcfData = reversedCF.map((cf: any, idx: number) => {
          const marketCap = marketCaps[idx];
          const fcf = getValueWithFallback(cf, fmpCashMap, getFcfValue);
          if (marketCap == null || fcf == null || fcf <= 0) return null;
          return marketCap / fcf;
        });
        return { labels, datasets: [{ label: 'Price / FCF', data: priceFcfData, backgroundColor: '#BA68C8' }] };
      }

      const cashPrimary = cashFlowStatements;
      chartSourceUpdates['price-fcf-chart'] = 'fmp';
      if (!keyMetrics || keyMetrics.length === 0 || !cashPrimary || cashPrimary.length === 0) return null;
      const startFiscal = new Date('2011-01-01');
      const reversedCF = [...cashPrimary].reverse().filter((cf: any) => {
        const d = new Date(cf?.date);
        return Number.isFinite(d.getTime()) && d >= startFiscal;
      });
      const reversedKM = [...keyMetrics].reverse().filter((km: any) => {
        const d = new Date(km?.date);
        return Number.isFinite(d.getTime()) && d >= startFiscal;
      });
      const alignedLength = Math.min(reversedCF.length, reversedKM.length);
      const cfAligned = reversedCF.slice(0, alignedLength);
      const kmAligned = reversedKM.slice(0, alignedLength);

      if (selectedTimeRange === 'TTM') {
        const marketCapQ = kmAligned.map((km: any) => toFiniteNumber(km?.marketCap));
        const fcfQ = cfAligned.map((cf: any) => getFcfValue(cf));
        const fcfTTM = rollingTTMSumNullable(fcfQ);
        const labels = cfAligned.map((cf: any) => formatPeriodLabel(cf.date)).slice(3);
        const priceFcfData = fcfTTM.map((ttmFcf, idx) => {
          const marketCap = marketCapQ[idx + 3];
          if (marketCap == null || ttmFcf == null || ttmFcf <= 0) return null;
          return marketCap / ttmFcf;
        });
        return { labels, datasets: [{ label: 'Price / FCF', data: priceFcfData, backgroundColor: '#BA68C8' }] };
      }

      const labels = cfAligned.map((cf: any) => formatPeriodLabel(cf.date));
      const priceFcfData = cfAligned.map((cf: any, idx: number) => {
        const marketCap = toFiniteNumber(kmAligned[idx]?.marketCap);
        const fcf = getFcfValue(cf);
        if (marketCap == null || fcf == null || fcf <= 0) return null;
        return marketCap / fcf;
      });
      return { labels, datasets: [{ label: 'Price / FCF', data: priceFcfData, backgroundColor: '#BA68C8' }] };
    };

    // Resolve fiscal availability + data source before building charts.
    try {
      const fiscalResults = await Promise.allSettled([
        fiscalIncomePromise,
        fiscalCashPromise,
        fiscalBalancePromise,
        fiscalRoicPromise,
        fiscalMarketCapPromise,
      ]);

      const fiscalIncome = settledValue(fiscalResults[0]);
      const fiscalCash = settledValue(fiscalResults[1]);
      const fiscalBalance = settledValue(fiscalResults[2]);
      fiscalRoicRows = (settledValue(fiscalResults[3]) ?? []) as Array<{ date: string; periodType: string; value?: number }>;
      fiscalMarketCapRows = (settledValue(fiscalResults[4]) ?? []) as Array<{ date: string; periodType: string; value?: number }>;

      const available = resolveFiscalAvailability(fiscalIncome, fiscalCash, fiscalBalance);
      fiscalIsAvailable = available;
      setFiscalAvailable(available);
      setDataSource(available ? 'fiscal' : 'fmp');

      fiscalIncomeRows = available
        ? (selectedTimeRange === 'Annual' ? fiscalIncome?.annual ?? [] : fiscalIncome?.quarter ?? [])
        : [];
      fiscalCashRows = available
        ? (selectedTimeRange === 'Annual' ? fiscalCash?.annual ?? [] : fiscalCash?.quarter ?? [])
        : [];
      fiscalBalanceRows = available
        ? (selectedTimeRange === 'Annual' ? fiscalBalance?.annual ?? [] : fiscalBalance?.quarter ?? [])
        : [];

      setRoicSource(available ? 'fiscal' : 'fmp');
    } catch {
      fiscalIsAvailable = false;
      setFiscalAvailable(false);
      setDataSource('fmp');
      setRoicSource('fmp');
    }

    // Load charts row-by-row (3 columns per row). Base data is fetched once.
    // Row 1: Cash & Debt, Revenue/Income, EPS
    try {
      const baseRow1 = await Promise.allSettled([balancePromise, incomePromise]);
      if (!isCurrent()) return snapshot;

      balanceSheets = settledValue(baseRow1[0]) ?? [];
      incomeStatements = settledValue(baseRow1[1]) ?? [];

      const cashDebtData = buildCashDebt();
      const revenueIncomeData = buildRevenueIncome();
      const epsData = buildEPS();

      snapshot = {
        ...snapshot,
        cashDebtData: cashDebtData ?? snapshot.cashDebtData,
        revenueIncomeData: revenueIncomeData ?? snapshot.revenueIncomeData,
        epsData: epsData ?? snapshot.epsData,
      };

      setCashDebtData(snapshot.cashDebtData);
      setRevenueIncomeData(snapshot.revenueIncomeData);
      setEpsData(snapshot.epsData);
      updateChartSources(chartSourceUpdates);

      // Show the first row as soon as it is ready on cold-load.
      if (isColdLoad) setLoading(false);
    } catch (error) {
      console.error('❌ Error fetching row 1:', error);
    }

    // Row 2: Cash Flow, Margins, ROIC
    try {
      const baseRow2 = await Promise.allSettled([cashFlowPromise, keyMetricsPromise]);
      if (!isCurrent()) return snapshot;

      cashFlowStatements = settledValue(baseRow2[0]) ?? [];
      keyMetrics = settledValue(baseRow2[1]) ?? [];

      const cashFlowData = buildCashFlow();
      const marginsData = buildMargins();
      const roicData = buildROIC();

      snapshot = {
        ...snapshot,
        cashFlowData: cashFlowData ?? snapshot.cashFlowData,
        marginsData: marginsData ?? snapshot.marginsData,
        roicData: roicData ?? snapshot.roicData,
      };

      setCashFlowData(snapshot.cashFlowData);
      setMarginsData(snapshot.marginsData);
      setRoicData(snapshot.roicData);
      updateChartSources(chartSourceUpdates);
    } catch (error) {
      console.error('❌ Error fetching row 2:', error);
    }

    // Row 3: Dividends, Shares, PE Ratio
    try {
      if (!isCurrent()) return snapshot;

      const dividendsData = buildDividends();
      const sharesData = buildShares();
      const peRatioData = buildPERatio();

      snapshot = {
        ...snapshot,
        dividendsData: dividendsData ?? snapshot.dividendsData,
        sharesData: sharesData ?? snapshot.sharesData,
        peRatioData: peRatioData ?? snapshot.peRatioData,
      };

      setDividendsData(snapshot.dividendsData);
      setSharesData(snapshot.sharesData);
      setPeRatioData(snapshot.peRatioData);
      updateChartSources(chartSourceUpdates);
    } catch (error) {
      console.error('❌ Error fetching row 3:', error);
    }

    // Row 4: Price / FCF
    try {
      if (!isCurrent()) return snapshot;

      const priceFcfData = buildPriceFCF();

      snapshot = {
        ...snapshot,
        priceFcfData: priceFcfData ?? snapshot.priceFcfData,
      };

      setPriceFcfData(snapshot.priceFcfData);
      updateChartSources(chartSourceUpdates);
    } catch (error) {
      console.error('❌ Error fetching row 4:', error);
    }

    return snapshot;
  };

  const containerStyle: React.CSSProperties = {
    // Keep the top gap controlled by the sticky top offset (below tabs-bar).
    padding: '0 0.5rem 1rem 0.5rem',
    backgroundColor: isDarkMode ? '#111827' : '#ffffff',
    minHeight: 'calc(100vh - 15rem)',
    color: isDarkMode ? '#e5e7eb' : '#1f2937',
    position: 'relative',
    width: '93%',
    marginLeft: '2.4rem',
    marginRight: '4rem',
    boxSizing: 'border-box',
  };

  const refreshingOverlayStyle: React.CSSProperties = {
    position: 'absolute',
    inset: 0,
    backgroundColor: isDarkMode ? 'rgba(17, 24, 39, 0.65)' : 'rgba(255, 255, 255, 0.65)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10,
    pointerEvents: 'none',
    fontWeight: 600,
  };

  const loadingOverlayStyle: React.CSSProperties = {
    position: 'absolute',
    inset: 0,
    backgroundColor: isDarkMode ? '#111827' : '#ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 20,
  };

  const loadingSpinnerStyle: React.CSSProperties = {
    width: '44px',
    height: '44px',
    borderRadius: '9999px',
    border: `4px solid ${isDarkMode ? 'rgba(229, 231, 235, 0.25)' : 'rgba(17, 24, 39, 0.15)'}`,
    borderTopColor: isDarkMode ? '#e5e7eb' : '#0D0D0D',
    animation: 'key-metrics-spin 0.9s linear infinite',
  };

  const chartGridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '1.5rem',
    marginTop: '1rem',
  };

  const chartCardStyle: React.CSSProperties = {
    backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
    border: `1px solid ${isDarkMode ? '#374151' : '#D9D9D9'}`,
    borderRadius: '8px',
    padding: '1rem',
  };

  const chartHeaderStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem',
  };

  const showSourceLabels = Boolean(isAdmin);
  const sourceLabelStyle: React.CSSProperties = {
    fontSize: '0.7rem',
    fontWeight: 600,
    color: isDarkMode ? '#9CA3AF' : '#6B7280',
    marginLeft: '0.5rem',
    letterSpacing: '0.02em',
    textTransform: 'uppercase',
  };

  const timeRangeSelectorStyle: React.CSSProperties = {
    display: 'flex',
    gap: '0.5rem',
    justifyContent: 'center',
    border: `1px solid ${isDarkMode ? '#374151' : '#D9D9D9'}`,
    borderRadius: '8px',
    padding: '0.5rem',
  };

  const stickyTopStyle: React.CSSProperties = {
    position: 'sticky',
    // Keep the sticky wrapper flush under `tabs-bar` so it covers the area beneath it.
    // The 10px visual gap is created via padding (with background) to avoid seeing charts
    // during fast scrolling.
    top: 'var(--research-header-stack-height, 0px)',
    zIndex: 50,
    backgroundColor: isDarkMode ? '#111827' : '#ffffff',
    width: '100%',
    boxSizing: 'border-box',
    paddingTop: '15px',
    paddingBottom: '0px',
  };

  const timeRangeButtonStyle = (isSelected: boolean): React.CSSProperties => ({
    height: '30px',
    padding: '0 1.5rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: isSelected ? (isDarkMode ? '#374151' : '#0D0D0D') : 'transparent',
    color: isSelected ? '#F9FAFB' : (isDarkMode ? '#F9FAFB' : '#000000'),
    border: isSelected ? 'none' : (isDarkMode ? 'none' : '1px solid #D9D9D9'),
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '0.875rem',
    fontWeight: '500',
  });

  const legendItemStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    cursor: 'pointer',
    fontSize: '0.875rem',
  };

  const legendColorBoxStyle = (color: string, isVisible: boolean): React.CSSProperties => ({
    width: '12px',
    height: '12px',
    backgroundColor: isVisible ? color : '#D9D9D9',
    borderRadius: '2px',
    opacity: isVisible ? 1 : 0.6,
  });

  const expandIconStyle: React.CSSProperties = {
    cursor: 'pointer',
    padding: '0.25rem',
    background: 'none',
    border: 'none',
  };

  const modalOverlayStyle: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    // Must be above the sticky Header stack (Header uses zIndex ~1200).
    zIndex: 10000,
  };

  const modalContentStyle: React.CSSProperties = {
    backgroundColor: isDarkMode ? '#111827' : '#ffffff',
    border: `1px solid ${isDarkMode ? '#374151' : 'transparent'}`,
    borderRadius: '12px',
    padding: '2rem',
    width: '90%',
    maxWidth: '1200px',
    height: '85vh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    color: isDarkMode ? '#F9FAFB' : '#0D0D0D',
  };

  type ValueFormat = 'usdMillions' | 'usdPerShare' | 'percent' | 'sharesBillions' | 'multiple';

  const getValueFormatForChart = (chartId: string): ValueFormat => {
    if (chartId === 'margins-chart' || chartId === 'roic-chart') return 'percent';
    if (chartId === 'shares-chart') return 'sharesBillions';
    if (chartId === 'eps-chart') return 'usdPerShare';
    if (chartId === 'pe-ratio-chart' || chartId === 'price-fcf-chart') return 'multiple';
    // cash/debt, revenue/income, cash flow, dividends
    return 'usdMillions';
  };

  const coerceNumber = (value: unknown): number => {
    const num = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(num) ? num : NaN;
  };

  const toFiniteNumber = (value: unknown): number | null => {
    const num = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(num) ? num : null;
  };

  const getChartSourceLabel = (chartId: string): string => {
    if (expandedChart && chartId === expandedChart && expandedChartSource) {
      return expandedChartSource === 'fiscal' ? 'fiscal.ai' : 'FMP';
    }
    if (chartId === 'roic-chart') return roicSource === 'fiscal' ? 'fiscal.ai' : 'FMP';
    const source = chartSources[chartId] ?? (dataSource === 'fiscal' ? 'fiscal' : 'fmp');
    return source === 'fiscal' ? 'fiscal.ai' : 'FMP';
  };

  const fiscalCatalogRef = useRef<Promise<any[]> | null>(null);
  const getFiscalCatalog = () => {
    if (!fiscalCatalogRef.current) {
      fiscalCatalogRef.current = fetchFiscalStandardizedMetricsList();
    }
    return fiscalCatalogRef.current;
  };

  const formatUsdMillions = (millions: number) => {
    const abs = Math.abs(millions);
    if (!Number.isFinite(abs)) return '';
    if (abs >= 1000) {
      const billions = millions / 1000;
      const formatted = billions.toLocaleString('en-US', {
        maximumFractionDigits: billions >= 100 ? 0 : 1,
        minimumFractionDigits: billions >= 100 ? 0 : 1,
      });
      return `$${formatted}B`;
    }

    const formatted = Math.round(millions).toLocaleString('en-US');
    return `$${formatted}M`;
  };

  const formatUsdPerShare = (dollars: number) => {
    if (!Number.isFinite(dollars)) return '';
    return `$${dollars.toLocaleString('en-US', { maximumFractionDigits: 2, minimumFractionDigits: 2 })}`;
  };

  const formatPercent = (
    percent: number,
    options: { maximumFractionDigits: number; minimumFractionDigits: number } = {
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
    }
  ) => {
    if (!Number.isFinite(percent)) return '';
    const { maximumFractionDigits, minimumFractionDigits } = options;
    return `${percent.toLocaleString('en-US', { maximumFractionDigits, minimumFractionDigits })}%`;
  };

  const formatSharesBillions = (billions: number) => {
    if (!Number.isFinite(billions)) return '';
    return `${billions.toLocaleString('en-US', { maximumFractionDigits: 1, minimumFractionDigits: 0 })}B`;
  };

  const formatMultiple = (value: number) => {
    if (!Number.isFinite(value)) return '';
    return `${value.toLocaleString('en-US', { maximumFractionDigits: 1, minimumFractionDigits: 0 })}`;
  };

  const formatChartValue = (chartId: string, value: unknown) => {
    const num = coerceNumber(value);
    if (!Number.isFinite(num)) return '';
    const format = getValueFormatForChart(chartId);
    switch (format) {
      case 'percent':
        if (chartId === 'roic-chart') return formatPercent(num, { maximumFractionDigits: 1, minimumFractionDigits: 1 });
        return formatPercent(num);
      case 'sharesBillions':
        return formatSharesBillions(num);
      case 'usdPerShare':
        return formatUsdPerShare(num);
      case 'multiple':
        return formatMultiple(num);
      case 'usdMillions':
      default:
        return formatUsdMillions(num);
    }
  };

  const deepMerge = (base: any, override: any): any => {
    if (base === null || base === undefined) return override;
    if (override === null || override === undefined) return base;

    const isPlainObject = (v: any) => v && typeof v === 'object' && !Array.isArray(v);
    if (!isPlainObject(base) || !isPlainObject(override)) return override;

    const merged: any = { ...base };
    for (const key of Object.keys(override)) {
      merged[key] = deepMerge(base[key], override[key]);
    }
    return merged;
  };

  const getChartOptions = (chartId: string, customOptions?: any) => {
    const baseOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          callbacks: {
            label: (context: any) => {
              const label = context?.dataset?.label ? `${context.dataset.label}: ` : '';
              const rawValue = context?.parsed?.y ?? context?.raw;
              return `${label}${formatChartValue(chartId, rawValue)}`;
            },
          },
        },
      },
      scales: {
        x: {
          ticks: {
            color: isDarkMode ? '#F9FAFB' : '#6b7280',
          },
        },
        y: {
          position: 'right',
          beginAtZero: true,
          grid: {
            color: isDarkMode ? '#374151' : '#e5e7eb',
          },
          ticks: {
            color: isDarkMode ? '#F9FAFB' : '#6b7280',
            callback: (value: any) => formatChartValue(chartId, value),
          },
        },
      },
    };

    return deepMerge(baseOptions, customOptions);
  };

  const getChartCanvas = (chartId: string): HTMLCanvasElement | null => {
    const root = document.getElementById(chartId);
    if (!root) return null;
    const canvas = root.querySelector('canvas');
    if (!(canvas instanceof HTMLCanvasElement)) return null;
    return canvas;
  };

  const toDataUrlWithFallback = (canvas: HTMLCanvasElement, preferredType: string, quality: number): { type: string; dataUrl: string } => {
    try {
      const url = canvas.toDataURL(preferredType as any, quality as any);
      if (typeof url === 'string' && url.startsWith(`data:${preferredType}`)) {
        return { type: preferredType, dataUrl: url };
      }
    } catch {
      // ignore and fall back
    }

    const fallbackType = 'image/png';
    return { type: fallbackType, dataUrl: canvas.toDataURL(fallbackType) };
  };

  const makeThumbDataUrl = async (canvas: HTMLCanvasElement, maxWidthPx: number, quality: number): Promise<string> => {
    const srcW = canvas.width;
    const srcH = canvas.height;
    if (!srcW || !srcH) return toDataUrlWithFallback(canvas, 'image/webp', quality).dataUrl;

    const scale = Math.min(1, maxWidthPx / srcW);
    const dstW = Math.max(1, Math.floor(srcW * scale));
    const dstH = Math.max(1, Math.floor(srcH * scale));

    const thumb = document.createElement('canvas');
    thumb.width = dstW;
    thumb.height = dstH;
    const ctx = thumb.getContext('2d');
    if (!ctx) return toDataUrlWithFallback(canvas, 'image/webp', quality).dataUrl;

    ctx.drawImage(canvas, 0, 0, dstW, dstH);
    return toDataUrlWithFallback(thumb, 'image/webp', quality).dataUrl;
  };

  const saveChartBookmark = async (chartId: string, title: string) => {
    if (!canPersistScreenshots) {
      onNotify?.('Please login to save screenshots.', 'error');
      return;
    }

    if (savingBookmarkByChartId[chartId]) return;

    // While the icon is in the 30s "saved" state, ignore repeat clicks.
    // After it returns to default, allow saving again.
    if (bookmarkFlashByChartId[chartId]) return;

    setSavingBookmarkByChartId((prev) => ({ ...prev, [chartId]: true }));

    try {
      const canvas = getChartCanvas(chartId);
      if (!canvas) {
        onNotify?.('Chart is not ready to capture yet.', 'error');
        return;
      }

      const full = toDataUrlWithFallback(canvas, 'image/png', 1);
      // Thumbnails are displayed in large tiles; store a higher-res thumb for clarity.
      const thumbDataUrl = await makeThumbDataUrl(canvas, 1100, 0.82);

      await adminApi.createResearchDeskScreenshot(authToken as string, {
        userId: resolvedUserId,
        sourcePage: 'Key Metrics',
        stockSymbol: selectedStock,
        title,
        imageDataUrl: full.dataUrl,
        thumbDataUrl,
      });

      setBookmarkedByChartId((prev) => ({ ...prev, [chartId]: true }));
      triggerBookmarkFlash(chartId);
      void `${selectedStock} • ${title} • Screenshot saved`;
    } catch (e: any) {
      if (isUnauthorized(e)) {
        onUnauthorized?.();
        return;
      }
      onNotify?.(e?.message || 'Failed saving screenshot', 'error');
    } finally {
      setSavingBookmarkByChartId((prev) => ({ ...prev, [chartId]: false }));
    }
  };

  const renderChart = (
    id: string,
    title: string,
    data: ChartData | null,
    type: 'bar' | 'line',
    legendItems?: Array<{ label: string; color: string; visible: boolean; onClick: () => void }>,
    customOptions?: any
  ) => {
    if (!data) return null;

    const zoomInIcon = isDarkMode ? '/images/darkmode/zoomin.svg' : '/images/zoomin.svg';
    const bookmarkEmptyIcon = isDarkMode ? '/images/darkmode/bookmark_empy.svg' : '/images/bookmark_empy.svg';

    const ChartComponent = type === 'bar' ? Bar : Line;

    const chartOptions = getChartOptions(id, customOptions);

    return (
      <div style={chartCardStyle} id={id}>
        <div style={chartHeaderStyle}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', transform: 'translateY(-3px)' }}>
              <button 
                style={expandIconStyle}
                onClick={() => setExpandedChart(id)}
                title="Expand"
              >
                <img src={zoomInIcon} alt="Expand" style={{ width: '24px', height: '24px' }} />
              </button>
              <h3 style={{ fontSize: '1rem', fontWeight: '600', margin: 0 }}>{title}</h3>
              {showSourceLabels ? <span style={sourceLabelStyle}>{getChartSourceLabel(id)}</span> : null}
            </div>
            <button
              style={{
                background: 'transparent',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                padding: '0.25rem',
                outline: 'none',
                boxShadow: 'none',
                opacity: savingBookmarkByChartId[id] ? 0.5 : 1,
                transition: 'background-color 120ms ease',
              }}
              title="Bookmark"
              disabled={savingBookmarkByChartId[id]}
              onClick={() => saveChartBookmark(id, `${title} (${selectedTimeRange})`)}
            >
              <img
                  src={bookmarkFlashByChartId[id] ? '/images/bookmark_fill.svg' : bookmarkEmptyIcon}
                alt="Bookmark"
                style={{
                  width: '16px',
                  height: '16px',
                }}
              />
            </button>
          </div>
        </div>

        <div
          style={{ height: '250px', marginBottom: '1rem', cursor: 'pointer' }}
          role="button"
          tabIndex={0}
          aria-label={`Expand ${title}`}
          onClick={() => setExpandedChart(id)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setExpandedChart(id);
            }
          }}
        >
          <ChartComponent
            data={data}
            options={chartOptions}
            plugins={[keyMetricsCrosshairPlugin]}
          />
        </div>
        
        {legendItems && (
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            {legendItems.map((item) => (
              <div key={item.label} style={{ ...legendItemStyle, opacity: item.visible ? 1 : 0.5 }} onClick={item.onClick}>
                <span style={legendColorBoxStyle(item.color, item.visible)}></span>
                <span>{item.label}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div ref={containerRef} id="key-metrics-box" style={containerStyle}>
      {loading && (
        <div style={loadingOverlayStyle} aria-label="Loading" role="status">
          <div style={loadingSpinnerStyle} />
        </div>
      )}
      {!loading && isRefreshing && <div style={refreshingOverlayStyle}>Updating…</div>}

      {/* Sticky top section (up to and including time range selector) */}
      <div style={stickyTopStyle}>
        <div id="key-metrics-time-range" style={timeRangeSelectorStyle}>
          <button
            style={timeRangeButtonStyle(selectedTimeRange === 'Annual')}
            onClick={() => setSelectedTimeRange('Annual')}
          >
            Annual
          </button>
          <button
            style={timeRangeButtonStyle(selectedTimeRange === 'Quarterly')}
            onClick={() => setSelectedTimeRange('Quarterly')}
          >
            Quarterly
          </button>
          <button
            style={timeRangeButtonStyle(selectedTimeRange === 'TTM')}
            onClick={() => setSelectedTimeRange('TTM')}
          >
            TTM
          </button>
        </div>
      </div>

      {/* Charts Grid */}
      <div style={chartGridStyle}>
        {renderChart(
          'cash-debt-chart',
          'Cash & Debt',
          cashDebtData,
          'bar',
          [
            { label: 'Cash', color: '#07DBFA', visible: cashDebtVisible.cash, onClick: () => setCashDebtVisible({ ...cashDebtVisible, cash: !cashDebtVisible.cash }) },
            { label: 'Debt', color: '#FF9800', visible: cashDebtVisible.debt, onClick: () => setCashDebtVisible({ ...cashDebtVisible, debt: !cashDebtVisible.debt }) },
          ]
        )}

        {renderChart(
          'revenue-income-chart',
          'Revenue & Operating Income & Net Income',
          revenueIncomeData,
          'bar',
          [
            { label: 'Revenue', color: '#1F94FF', visible: revenueIncomeVisible.revenue, onClick: () => setRevenueIncomeVisible({ ...revenueIncomeVisible, revenue: !revenueIncomeVisible.revenue }) },
            { label: 'Operating Income', color: '#07DBFA', visible: revenueIncomeVisible.operatingIncome, onClick: () => setRevenueIncomeVisible({ ...revenueIncomeVisible, operatingIncome: !revenueIncomeVisible.operatingIncome }) },
            { label: 'Net Income', color: '#988AFC', visible: revenueIncomeVisible.netIncome, onClick: () => setRevenueIncomeVisible({ ...revenueIncomeVisible, netIncome: !revenueIncomeVisible.netIncome }) },
          ]
        )}

        {renderChart('eps-chart', 'EPS', epsData, 'bar')}

        {renderChart(
          'cash-flow-chart',
          'Operating Cash Flow & Free Cash Flow',
          cashFlowData,
          'bar',
          [
            { label: 'Operating Cash Flow', color: '#07DBFA', visible: cashFlowVisible.operating, onClick: () => setCashFlowVisible({ ...cashFlowVisible, operating: !cashFlowVisible.operating }) },
            { label: 'Free Cash Flow', color: '#009688', visible: cashFlowVisible.free, onClick: () => setCashFlowVisible({ ...cashFlowVisible, free: !cashFlowVisible.free }) },
          ]
        )}

        {renderChart(
          'margins-chart',
          'Gross Margin & Operating Margin & Net Margin & FCF Margin',
          marginsData,
          'bar',
          [
            { label: 'Gross Margin', color: '#9575CD', visible: marginsVisible.gross, onClick: () => setMarginsVisible({ ...marginsVisible, gross: !marginsVisible.gross }) },
            { label: 'Operating Margin', color: '#8BC34A', visible: marginsVisible.operating, onClick: () => setMarginsVisible({ ...marginsVisible, operating: !marginsVisible.operating }) },
            { label: 'Net Margin', color: '#FF9800', visible: marginsVisible.net, onClick: () => setMarginsVisible({ ...marginsVisible, net: !marginsVisible.net }) },
            { label: 'FCF Margin', color: '#07DBFA', visible: marginsVisible.fcf, onClick: () => setMarginsVisible({ ...marginsVisible, fcf: !marginsVisible.fcf }) },
          ]
        )}

        {renderChart('roic-chart', 'ROIC', roicData, 'bar')}

        {renderChart('dividends-chart', 'Dividends', dividendsData, 'bar')}

        {renderChart('shares-chart', 'Weighted Avg Shares (Diluted)', sharesData, 'bar')}

        {renderChart('pe-ratio-chart', 'PE Ratio', peRatioData, 'bar')}

        {renderChart('price-fcf-chart', 'Price / FCF', priceFcfData, 'bar', undefined, {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: false,
            },
          },
          scales: {
            y: {
              position: 'right',
              min: 0,
              ticks: {
                stepSize: 5,
              },
            },
          },
        })}
      </div>

      {/* Expanded Chart Modal */}
      {expandedChart && (
        <div style={modalOverlayStyle} onClick={() => setExpandedChart(null)}>
          <div style={modalContentStyle} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexShrink: 0 }}>
              <h2 style={{ fontSize: '1.5rem', fontWeight: '600', margin: 0 }}>
                {expandedChart === 'cash-debt-chart' && 'Cash & Debt'}
                {expandedChart === 'revenue-income-chart' && 'Revenue & Operating Income & Net Income'}
                {expandedChart === 'eps-chart' && 'EPS'}
                {expandedChart === 'cash-flow-chart' && 'Operating Cash Flow & Free Cash Flow'}
                {expandedChart === 'margins-chart' && 'Gross Margin & Operating Margin & Net Margin & FCF Margin'}
                {expandedChart === 'roic-chart' && 'ROIC'}
                {expandedChart === 'dividends-chart' && 'Dividends'}
                {expandedChart === 'shares-chart' && 'Weighted Avg Shares (Diluted)'}
                {expandedChart === 'pe-ratio-chart' && 'PE Ratio'}
                {expandedChart === 'price-fcf-chart' && 'Price / FCF'}
                {showSourceLabels && expandedChart ? (
                  <span style={sourceLabelStyle}>{getChartSourceLabel(expandedChart)}</span>
                ) : null}
                <span
                  style={{
                    marginLeft: '0.75rem',
                    fontSize: '0.85rem',
                    fontWeight: 700,
                    padding: '0.25rem 0.6rem',
                    borderRadius: '9999px',
                    border: `1px solid ${isDarkMode ? 'rgba(229, 231, 235, 0.22)' : '#E5E7EB'}`,
                    background: isDarkMode ? 'rgba(17, 24, 39, 0.35)' : '#F3F4F6',
                    color: isDarkMode ? '#E5E7EB' : '#111827',
                    verticalAlign: 'middle',
                    letterSpacing: '0.02em',
                    textTransform: 'uppercase',
                  }}
                  title="Selected time range"
                >
                  {selectedTimeRange}
                </span>
              </h2>
              <button
                onClick={() => setExpandedChart(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '2rem',
                  cursor: 'pointer',
                  padding: 0,
                }}
              >
                {isDarkMode ? (
                  <img src="/images/darkmode/close.svg" alt="Close" style={{ width: 20, height: 20, display: 'block' }} />
                ) : (
                  '×'
                )}
              </button>
            </div>
            <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
              {expandedChart === 'cash-debt-chart' && cashDebtData && (
                <>
                  <div style={{ flex: 1, minHeight: 0 }}>
                    <Bar
                      data={cashDebtData}
                      options={getChartOptions('cash-debt-chart')}
                      plugins={[keyMetricsCrosshairPlugin]}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap', marginTop: '1rem', flexShrink: 0 }}>
                    <div style={{ ...legendItemStyle, opacity: cashDebtVisible.cash ? 1 : 0.5 }} onClick={() => setCashDebtVisible({ ...cashDebtVisible, cash: !cashDebtVisible.cash })}>
                      <span style={legendColorBoxStyle('#07DBFA', cashDebtVisible.cash)}></span>
                      <span>Cash</span>
                    </div>
                    <div style={{ ...legendItemStyle, opacity: cashDebtVisible.debt ? 1 : 0.5 }} onClick={() => setCashDebtVisible({ ...cashDebtVisible, debt: !cashDebtVisible.debt })}>
                      <span style={legendColorBoxStyle('#FF9800', cashDebtVisible.debt)}></span>
                      <span>Debt</span>
                    </div>
                  </div>
                </>
              )}

              {expandedChart === 'revenue-income-chart' && revenueIncomeData && (
                <>
                  <div style={{ flex: 1, minHeight: 0 }}>
                    <Bar
                      data={revenueIncomeData}
                      options={getChartOptions('revenue-income-chart')}
                      plugins={[keyMetricsCrosshairPlugin]}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap', marginTop: '1rem', flexShrink: 0 }}>
                    <div style={{ ...legendItemStyle, opacity: revenueIncomeVisible.revenue ? 1 : 0.5 }} onClick={() => setRevenueIncomeVisible({ ...revenueIncomeVisible, revenue: !revenueIncomeVisible.revenue })}>
                      <span style={legendColorBoxStyle('#1F94FF', revenueIncomeVisible.revenue)}></span>
                      <span>Revenue</span>
                    </div>
                    <div style={{ ...legendItemStyle, opacity: revenueIncomeVisible.operatingIncome ? 1 : 0.5 }} onClick={() => setRevenueIncomeVisible({ ...revenueIncomeVisible, operatingIncome: !revenueIncomeVisible.operatingIncome })}>
                      <span style={legendColorBoxStyle('#07DBFA', revenueIncomeVisible.operatingIncome)}></span>
                      <span>Operating Income</span>
                    </div>
                    <div style={{ ...legendItemStyle, opacity: revenueIncomeVisible.netIncome ? 1 : 0.5 }} onClick={() => setRevenueIncomeVisible({ ...revenueIncomeVisible, netIncome: !revenueIncomeVisible.netIncome })}>
                      <span style={legendColorBoxStyle('#988AFC', revenueIncomeVisible.netIncome)}></span>
                      <span>Net Income</span>
                    </div>
                  </div>
                </>
              )}

              {expandedChart === 'eps-chart' && epsData && (
                <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
                  {selectedTimeRange === 'Quarterly' || selectedTimeRange === 'Annual' || selectedTimeRange === 'TTM' ? (
                    epsExpandedData ? (
                      <Bar data={epsExpandedData} options={getChartOptions('eps-chart')} plugins={[keyMetricsCrosshairPlugin]} />
                    ) : (
                      <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <div style={loadingSpinnerStyle} />
                      </div>
                    )
                  ) : (
                    <Bar data={epsData} options={getChartOptions('eps-chart')} plugins={[keyMetricsCrosshairPlugin]} />
                  )}
                  {epsExpandedLoading && (
                    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                      <div style={loadingSpinnerStyle} />
                    </div>
                  )}
                </div>
              )}

              {expandedChart === 'cash-flow-chart' && cashFlowData && (
                <>
                  <div style={{ flex: 1, minHeight: 0 }}>
                    <Bar
                      data={cashFlowData}
                      options={getChartOptions('cash-flow-chart')}
                      plugins={[keyMetricsCrosshairPlugin]}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap', marginTop: '1rem', flexShrink: 0 }}>
                    <div style={{ ...legendItemStyle, opacity: cashFlowVisible.operating ? 1 : 0.5 }} onClick={() => setCashFlowVisible({ ...cashFlowVisible, operating: !cashFlowVisible.operating })}>
                      <span style={legendColorBoxStyle('#07DBFA', cashFlowVisible.operating)}></span>
                      <span>Operating Cash Flow</span>
                    </div>
                    <div style={{ ...legendItemStyle, opacity: cashFlowVisible.free ? 1 : 0.5 }} onClick={() => setCashFlowVisible({ ...cashFlowVisible, free: !cashFlowVisible.free })}>
                      <span style={legendColorBoxStyle('#009688', cashFlowVisible.free)}></span>
                      <span>Free Cash Flow</span>
                    </div>
                  </div>
                </>
              )}

              {expandedChart === 'margins-chart' && marginsData && (
                <>
                  <div style={{ flex: 1, minHeight: 0 }}>
                    <Bar
                      data={marginsData}
                      options={getChartOptions('margins-chart')}
                      plugins={[keyMetricsCrosshairPlugin]}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap', marginTop: '1rem', flexShrink: 0 }}>
                    <div style={{ ...legendItemStyle, opacity: marginsVisible.gross ? 1 : 0.5 }} onClick={() => setMarginsVisible({ ...marginsVisible, gross: !marginsVisible.gross })}>
                      <span style={legendColorBoxStyle('#9575CD', marginsVisible.gross)}></span>
                      <span>Gross Margin</span>
                    </div>
                    <div style={{ ...legendItemStyle, opacity: marginsVisible.operating ? 1 : 0.5 }} onClick={() => setMarginsVisible({ ...marginsVisible, operating: !marginsVisible.operating })}>
                      <span style={legendColorBoxStyle('#8BC34A', marginsVisible.operating)}></span>
                      <span>Operating Margin</span>
                    </div>
                    <div style={{ ...legendItemStyle, opacity: marginsVisible.net ? 1 : 0.5 }} onClick={() => setMarginsVisible({ ...marginsVisible, net: !marginsVisible.net })}>
                      <span style={legendColorBoxStyle('#FF9800', marginsVisible.net)}></span>
                      <span>Net Margin</span>
                    </div>
                    <div style={{ ...legendItemStyle, opacity: marginsVisible.fcf ? 1 : 0.5 }} onClick={() => setMarginsVisible({ ...marginsVisible, fcf: !marginsVisible.fcf })}>
                      <span style={legendColorBoxStyle('#07DBFA', marginsVisible.fcf)}></span>
                      <span>FCF Margin</span>
                    </div>
                  </div>
                </>
              )}

              {expandedChart === 'roic-chart' && roicData && (
                <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
                  {roicExpandedData ? (
                    <Bar data={roicExpandedData} options={getChartOptions('roic-chart')} plugins={[keyMetricsCrosshairPlugin]} />
                  ) : (
                    <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <div style={loadingSpinnerStyle} />
                    </div>
                  )}

                  {roicExpandedLoading && (
                    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                      <div style={loadingSpinnerStyle} />
                    </div>
                  )}
                </div>
              )}

              {expandedChart === 'dividends-chart' && dividendsData && (
                <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
                  {dividendsExpandedData ? (
                    <Bar data={dividendsExpandedData} options={getChartOptions('dividends-chart')} plugins={[keyMetricsCrosshairPlugin]} />
                  ) : (
                    <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <div style={loadingSpinnerStyle} />
                    </div>
                  )}

                  {dividendsExpandedLoading && (
                    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                      <div style={loadingSpinnerStyle} />
                    </div>
                  )}
                </div>
              )}

              {expandedChart === 'shares-chart' && sharesData && (
                <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
                  {sharesExpandedData ? (
                    <Bar data={sharesExpandedData} options={getChartOptions('shares-chart')} plugins={[keyMetricsCrosshairPlugin]} />
                  ) : (
                    <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <div style={loadingSpinnerStyle} />
                    </div>
                  )}

                  {sharesExpandedLoading && (
                    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                      <div style={loadingSpinnerStyle} />
                    </div>
                  )}
                </div>
              )}

              {expandedChart === 'pe-ratio-chart' && peRatioData && (
                <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
                  {peRatioExpandedData ? (
                    <Bar data={peRatioExpandedData} options={getChartOptions('pe-ratio-chart')} plugins={[keyMetricsCrosshairPlugin]} />
                  ) : (
                    <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <div style={loadingSpinnerStyle} />
                    </div>
                  )}

                  {peRatioExpandedLoading && (
                    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                      <div style={loadingSpinnerStyle} />
                    </div>
                  )}
                </div>
              )}

              {expandedChart === 'price-fcf-chart' && priceFcfData && (
                <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
                  {priceFcfExpandedData ? (
                    <Bar
                      data={priceFcfExpandedData}
                      options={getChartOptions('price-fcf-chart', {
                        scales: {
                          y: {
                            position: 'right',
                            min: 0,
                            ticks: {
                              stepSize: 5,
                            },
                          },
                        },
                      })}
                      plugins={[keyMetricsCrosshairPlugin]}
                    />
                  ) : (
                    <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <div style={loadingSpinnerStyle} />
                    </div>
                  )}

                  {priceFcfExpandedLoading && (
                    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                      <div style={loadingSpinnerStyle} />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes key-metrics-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>

      {/* New Note Button */}
      <DataSourceAttribution isDarkMode={isDarkMode} />

      <FloatingNewNoteButton
        visible={showFloatingButton}
        onClick={openNewNote}
        label="New Note"
        backgroundColor={isDarkMode ? '#374151' : undefined}
      />

      {/* Note Modal */}
      {isNoteModalOpen && (
        <div
          id="note-modal-overlay"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10001,
          }}
          onClick={() => setIsNoteModalOpen(false)}
        >
          {/* CSS Styles - copied from Summary modal */}
          <style>{`
            .note-modal-quill .quill {
              display: flex;
              flex-direction: column;
              height: 100%;
            }
            .note-modal-quill .ql-toolbar {
              order: 2;
              border: none !important;
              padding: 8px 0 !important;
              display: flex !important;
              gap: 8px !important;
              flex-wrap: nowrap !important;
              align-items: center !important;
              flex-shrink: 0 !important;
              min-height: 40px !important;
              background: white !important;
              margin-top: 8px !important;
            }
            .note-modal-quill .ql-container {
              order: 1;
              flex: 1;
              background-color: #ffffff;
              border: none !important;
              overflow: hidden;
            }
            .note-modal-quill .ql-editor {
              border-radius: 6px;
              min-height: 100px;
              max-height: 150px;
              overflow-y: auto;
              direction: auto;
              text-align: start;
              font-size: 18px;
            }
            .note-modal-quill .ql-editor p {
              direction: auto;
              text-align: start;
            }
            .note-modal-quill .ql-toolbar .ql-formats {
              margin-right: 0 !important;
              display: inline-flex !important;
              gap: 8px !important;
            }
            #note-toolbar button,
            #note-toolbar .ql-picker {
              width: 20px !important;
              height: 20px !important;
              padding: 0 !important;
              display: inline-flex !important;
              align-items: center !important;
              justify-content: center !important;
            }
            #note-toolbar .ql-picker-label {
              padding: 0 !important;
              width: 24px !important;
              height: 24px !important;
            }
            #note-toolbar .ql-align .ql-picker-label {
              width: 32px !important;
              height: 32px !important;
              padding: 0 !important;
            }
            #note-toolbar .ql-align .ql-picker-label svg {
              display: none !important;
            }
            #note-toolbar .ql-align .ql-picker-label::before {
              content: '' !important;
              display: block !important;
              width: 28px !important;
              height: 28px !important;
              background-image: url('/images/align.svg') !important;
              background-size: contain !important;
              background-repeat: no-repeat !important;
              background-position: center !important;
            }
            #note-toolbar .ql-undo img,
            #note-toolbar .ql-redo img {
              width: 18px !important;
              height: 18px !important;
            }
            #note-toolbar button img {
              width: 18px !important;
              height: 18px !important;
            }
            #note-toolbar .ql-fontDecrease img,
            #note-toolbar .ql-fontIncrease img {
              width: 24px !important;
              height: 24px !important;
            }
            #note-toolbar .ql-picker-label::before {
              width: 16px !important;
              height: 16px !important;
            }
            .note-modal-quill .ql-toolbar .ql-picker-options {
              max-height: 200px !important;
              overflow-y: auto !important;
            }
            .note-modal-quill .ql-toolbar button {
              width: 20px !important;
              height: 20px !important;
              padding: 4px !important;
            }
            .note-modal-quill .ql-toolbar button svg {
              width: 18px;
              height: 18px;
            }
            #note-toolbar .ql-mention {
              font-size: 0 !important;
              padding: 0 !important;
            }
            #note-toolbar .ql-mention::before {
              content: '' !important;
              display: block !important;
              width: 18px !important;
              height: 18px !important;
              background-image: url('/images/mention.svg') !important;
              background-size: contain !important;
              background-repeat: no-repeat !important;
              background-position: center !important;
            }
            #note-toolbar .ql-emoji {
              font-size: 16px;
            }
            #note-toolbar .ql-color .ql-picker-label,
            #note-toolbar .ql-background .ql-picker-label {
              padding: 0 !important;
              width: 28px !important;
              height: 28px !important;
            }
            #note-toolbar .ql-color .ql-picker-label svg,
            #note-toolbar .ql-background .ql-picker-label svg {
              display: none !important;
            }
            #note-toolbar .ql-color .ql-picker-label::before {
              content: '' !important;
              display: block !important;
              width: 20px !important;
              height: 20px !important;
              background-image: url('/images/font-color.svg') !important;
              background-size: contain !important;
              background-repeat: no-repeat !important;
              background-position: center !important;
              margin: 2px !important;
            }
            #note-toolbar .ql-background .ql-picker-label::before {
              content: '' !important;
              display: block !important;
              width: 18px !important;
              height: 18px !important;
              background-image: url('/images/edit-editor.svg?v=20260303') !important;
              background-size: contain !important;
              background-repeat: no-repeat !important;
              background-position: center !important;
              margin: 2px !important;
            }
            #note-toolbar .ql-link svg {
              display: none !important;
            }
            #note-toolbar .ql-link::before {
              content: '' !important;
              display: block !important;
              width: 18px !important;
              height: 18px !important;
              background-image: url('/images/insert_link.svg') !important;
              background-size: contain !important;
              background-repeat: no-repeat !important;
              background-position: center !important;
            }
            #note-toolbar .ql-list svg {
              display: none !important;
            }
            #note-toolbar .ql-list::before {
              content: '' !important;
              display: block !important;
              width: 18px !important;
              height: 18px !important;
              background-image: url('/images/list_check_line.svg') !important;
              background-size: contain !important;
              background-repeat: no-repeat !important;
              background-position: center !important;
            }
            #note-toolbar .ql-indent svg {
              display: none !important;
            }
            #note-toolbar .ql-indent[value="-1"]::before {
              content: '' !important;
              display: block !important;
              width: 18px !important;
              height: 18px !important;
              background-image: url('/images/list-indent-increase.svg') !important;
              background-size: contain !important;
              background-repeat: no-repeat !important;
              background-position: center !important;
            }
            #note-toolbar .ql-indent[value="+1"]::before {
              content: '' !important;
              display: block !important;
              width: 18px !important;
              height: 18px !important;
              background-image: url('/images/list-indent-decrease.svg') !important;
              background-size: contain !important;
              background-repeat: no-repeat !important;
              background-position: center !important;
            }
            #note-toolbar .ql-bold svg {
              display: none !important;
            }
            #note-toolbar .ql-bold::before {
              content: '' !important;
              display: block !important;
              width: 18px !important;
              height: 18px !important;
              background-image: url('/images/bold_line.svg') !important;
              background-size: contain !important;
              background-repeat: no-repeat !important;
              background-position: center !important;
            }
            #note-toolbar .ql-italic svg {
              display: none !important;
            }
            #note-toolbar .ql-italic::before {
              content: '' !important;
              display: block !important;
              width: 18px !important;
              height: 18px !important;
              background-image: url('/images/italic_line.svg') !important;
              background-size: contain !important;
              background-repeat: no-repeat !important;
              background-position: center !important;
            }
            #note-toolbar .ql-underline svg {
              display: none !important;
            }
            #note-toolbar .ql-underline::before {
              content: '' !important;
              display: block !important;
              width: 18px !important;
              height: 18px !important;
              background-image: url('/images/underline_line.svg') !important;
              background-size: contain !important;
              background-repeat: no-repeat !important;
              background-position: center !important;
            }
            #note-toolbar .ql-strike svg {
              display: none !important;
            }
            #note-toolbar .ql-strike::before {
              content: '' !important;
              display: block !important;
              width: 18px !important;
              height: 18px !important;
              background-image: url('/images/strikethrough_line.svg') !important;
              background-size: contain !important;
              background-repeat: no-repeat !important;
              background-position: center !important;
            }
            .note-modal-quill .ql-tooltip {
              position: absolute !important;
              z-index: 10004 !important;
              left: 50% !important;
              transform: translateX(-50%) !important;
              bottom: 50px !important;
            }
            .note-modal-quill .ql-tooltip.ql-editing {
              left: 50% !important;
              transform: translateX(-50%) !important;
            }
            #note-toolbar .ql-emoji {
              font-size: 0 !important;
              padding: 0 !important;
            }
            #note-toolbar .ql-emoji::before {
              content: '' !important;
              display: block !important;
              width: 18px !important;
              height: 18px !important;
              background-image: url('/images/smile.svg') !important;
              background-size: contain !important;
              background-repeat: no-repeat !important;
              background-position: center !important;
              font-size: 16px !important;
            }
            #note-toolbar .ql-image svg {
              display: none !important;
            }
            #note-toolbar .ql-image::before {
              content: '' !important;
              display: block !important;
              width: 18px !important;
              height: 18px !important;
              background-image: url('/images/insertimage.svg?v=20260303') !important;
              background-size: contain !important;
              background-repeat: no-repeat !important;
              background-position: center !important;
            }
          `}</style>

          <div
            id="add-note-modal"
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '6px',
              width: '884px',
              height: '337px',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
              padding: '10px 12px',
              gap: '6px',
              opacity: 1,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              id="note-modal-header"
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                paddingBottom: '0.5rem',
              }}
            >
              <h2 style={{ fontSize: '1rem', fontWeight: '600', margin: 0 }}>New Note</h2>
              <button
                onClick={() => setIsNoteModalOpen(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  color: '#6b7280',
                  padding: 0,
                  width: '24px',
                  height: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                ×
              </button>
            </div>

            <div
              id="note-content-wrapper"
              style={{
                border: '1px solid #ECECF4',
                borderRadius: '6px',
                padding: '12px',
                paddingBottom: '0px',
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                gap: '6px',
                overflow: 'visible',
              }}
            >
              <div
                id="note-editor-content"
                style={{
                  flex: 1,
                  overflow: 'visible',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <div className="note-modal-quill" style={{ height: '200px', display: 'flex', flexDirection: 'column' }}>
                  <div style={{ flex: 1, minHeight: 0 }}>
                    <ReactQuill
                      ref={quillRef}
                      value={noteContent}
                      onChange={setNoteContent}
                      placeholder={
                        noteCategory.trim().length > 0
                          ? `Here you can write about ${noteCategory}`
                          : 'Here you can write about Key Metrics'
                      }
                      style={{
                        height: '100%',
                        border: 'none',
                      }}
                      modules={quillModules}
                      formats={quillFormats}
                    />
                  </div>

                  <div
                    id="note-toolbar"
                    style={{
                      paddingTop: '18px',
                      paddingBottom: '12px',
                      display: 'flex',
                      gap: '0px',
                      flexWrap: 'nowrap',
                      background: 'white',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      width: '100%',
                      borderBottom: '1px solid #ECECF4',
                      marginBottom: '2px',
                    }}
                  >
                    <span className="ql-formats">
                      <button className="ql-undo" title="Undo">
                        <img src="/images/undo-icon.svg" alt="Undo" style={{ width: '18px', height: '18px' }} />
                      </button>
                      <button className="ql-redo" title="Redo">
                        <img
                          src="/images/undo-icon.svg"
                          alt="Redo"
                          style={{ width: '18px', height: '18px', transform: 'scaleX(-1)' }}
                        />
                      </button>
                    </span>
                    <span
                      style={{
                        display: 'inline-block',
                        width: '1px',
                        height: '20px',
                        backgroundColor: '#D0D0D0',
                        margin: '0 8px',
                        flexShrink: 0,
                      }}
                    ></span>
                    <span className="ql-formats">
                      <button className="ql-list" value="ordered" title="Ordered List"></button>
                      <button className="ql-list" value="bullet" title="Bullet List"></button>
                    </span>
                    <span className="ql-formats">
                      <select className="ql-align" title="Text Alignment"></select>
                    </span>
                    <span
                      style={{
                        display: 'inline-block',
                        width: '1px',
                        height: '20px',
                        backgroundColor: '#D0D0D0',
                        margin: '0 8px',
                        flexShrink: 0,
                      }}
                    ></span>
                    <span className="ql-formats">
                      <button className="ql-indent" value="+1" title="Indent Increase"></button>
                      <button className="ql-indent" value="-1" title="Indent Decrease"></button>
                    </span>
                    <span
                      style={{
                        display: 'inline-block',
                        width: '1px',
                        height: '20px',
                        backgroundColor: '#D0D0D0',
                        margin: '0 8px',
                        flexShrink: 0,
                      }}
                    ></span>
                    <span className="ql-formats">
                      <select className="ql-color" title="Text Color"></select>
                      <select className="ql-background" title="Background Color"></select>
                    </span>
                    <span className="ql-formats">
                      <button
                        className="ql-fontDecrease"
                        title="Decrease Font"
                        onClick={(e) => {
                          e.preventDefault();
                          const quill = quillRef.current?.getEditor?.();
                          applyQuillFontSizeStep(quill, -1);
                        }}
                        style={{ marginRight: '6px' }}
                      >
                        <img src="/images/a-arrow-down.svg" alt="A-" style={{ width: '24px', height: '24px' }} />
                      </button>
                      <button
                        className="ql-fontIncrease"
                        title="Increase Font"
                        onClick={(e) => {
                          e.preventDefault();
                          const quill = quillRef.current?.getEditor?.();
                          applyQuillFontSizeStep(quill, 1);
                        }}
                      >
                        <img src="/images/a-arrow-up.svg" alt="A+" style={{ width: '24px', height: '24px' }} />
                      </button>
                    </span>
                    <span
                      style={{
                        display: 'inline-block',
                        width: '1px',
                        height: '20px',
                        backgroundColor: '#D0D0D0',
                        margin: '0 8px',
                        flexShrink: 0,
                      }}
                    ></span>
                    <span className="ql-formats">
                      <button className="ql-bold" title="Bold"></button>
                      <button className="ql-italic" title="Italic"></button>
                      <button className="ql-underline" title="Underline"></button>
                      <button className="ql-strike" title="Strike"></button>
                    </span>
                    <span
                      style={{
                        display: 'inline-block',
                        width: '1px',
                        height: '20px',
                        backgroundColor: '#D0D0D0',
                        margin: '0 8px',
                        flexShrink: 0,
                      }}
                    ></span>
                    <span className="ql-formats">
                      <button className="ql-link" title="Insert Link"></button>
                      <button
                        className="ql-mention"
                        title="Mention (@)"
                        onClick={(e) => {
                          e.preventDefault();
                          const quill = document.querySelector('.note-modal-quill .ql-editor') as any;
                          if (quill) {
                            const selection = window.getSelection();
                            if (selection && selection.rangeCount > 0) {
                              const range = selection.getRangeAt(0);
                              const textNode = document.createTextNode('@');
                              range.insertNode(textNode);
                              range.setStartAfter(textNode);
                              range.setEndAfter(textNode);
                            }
                          }
                        }}
                      >
                        @
                      </button>
                      <button
                        className="ql-emoji"
                        title="Emoji"
                        onClick={(e) => {
                          e.preventDefault();
                          const quill = document.querySelector('.note-modal-quill .ql-editor') as any;
                          if (quill) {
                            const selection = window.getSelection();
                            if (selection && selection.rangeCount > 0) {
                              const range = selection.getRangeAt(0);
                              const textNode = document.createTextNode('😊');
                              range.insertNode(textNode);
                              range.setStartAfter(textNode);
                              range.setEndAfter(textNode);
                            }
                          }
                        }}
                      >
                        😊
                      </button>
                      <button className="ql-image" title="Insert Image"></button>
                    </span>
                  </div>
                </div>

                <div
                  style={{
                    width: '100%',
                    height: '1px',
                    backgroundColor: '#ECECF4',
                    margin: '1px 0',
                  }}
                ></div>
              </div>

              <div
                id="note-modal-footer"
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  paddingTop: '1rem',
                  borderBottom: '1px solid #ECECF4',
                  marginTop: '0rem',
                  padding: '0.5rem',
                  marginLeft: '-1rem',
                  marginRight: '-1rem',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <div ref={noteCategoryDropdownRef} style={noteCategoryDropdownWrapStyle}>
                    <button
                      type="button"
                      style={noteCategoryDropdownButtonStyle}
                      onClick={() => setIsNoteCategoryOpen((v) => !v)}
                      aria-haspopup="listbox"
                      aria-expanded={isNoteCategoryOpen}
                    >
                      {noteCategory || 'Associated With:'}
                    </button>
                    <span aria-hidden="true" style={noteCategoryDropdownArrowStyle}>
                      <img src="/images/arrow.svg" alt="" style={{ width: 18, height: 18, display: 'block' }} />
                    </span>
                    {isNoteCategoryOpen && (
                      <div role="listbox" aria-label="Associated With" style={noteCategoryDropdownMenuStyle}>
                        {NOTE_CATEGORIES.map((c, idx) => (
                          <div
                            key={c}
                            role="option"
                            aria-selected={noteCategory === c}
                            style={{
                              ...noteCategoryDropdownItemStyle,
                              borderTop: idx === 0 ? 'none' : '1px solid #ECECF4',
                            }}
                            onClick={() => {
                              setNoteCategory(c);
                              setIsNoteCategoryOpen(false);
                            }}
                          >
                            <span style={noteCategoryDropdownCheckboxStyle}>
                              {noteCategory === c ? <span style={noteCategoryDropdownCheckIconStyle} /> : null}
                            </span>
                            {c}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <button
                  disabled={!canAddNote}
                  onClick={async () => {
                    if (!canPersistNotes) {
                      onNotify?.('Please login to save notes.', 'error');
                      return;
                    }

                    const token = authToken as string;
                    const userId = resolvedUserId as number;
                    const title = extractTitleFromContent(noteContent);
                    if (!title) return;
                    const associatedWith = noteCategory.trim();
                    if (!associatedWith) return;

                    try {
                      await adminApi.createResearchDeskNote(token, {
                        userId,
                        title,
                        associatedWith,
                        stockSymbol: selectedStock,
                        html: noteContent ?? '',
                      });

                      setIsNoteModalOpen(false);
                      setNoteTitle('');
                      setNoteContent('');
                      setNoteCategory('Key Metrics');
                      onNotify?.(`${selectedStock} • Key Metrics • Saved`, 'success');
                    } catch (e: any) {
                      if (isUnauthorized(e)) {
                        onUnauthorized?.();
                      }
                    }
                  }}
                  style={{
                    padding: '0.5rem 1.5rem',
                    backgroundColor: '#000000',
                    color: '#ffffff',
                    border: '1px solid #ECECF4',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    cursor: canAddNote ? 'pointer' : 'not-allowed',
                    opacity: canAddNote ? 1 : 0.6,
                  }}
                >
                  Add Note
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResearchKeyMetricsScreen;

